import React, { useState, useRef, useEffect, useMemo } from "react";
import { api } from '../api/client';
import { useContacts } from '../contexts/ContactsContext';
import { useInvoices } from '../contexts/InvoicesContext';
import { loadSession, persistMessages, newSessionId, generateSessionTitle, saveSessionToDb, getLastDbSession, loadSessionFromDb } from '../services/aideySessionsService';
import { getTravelInfo, getBookingLinks, findSameDayInPersonMeetings, MODE_EMOJI, MODE_LABEL } from '../services/travelTimeService';
import { createGoogleMeet, formatMeetingForGoogleMeet, isGoogleMeetAvailable } from '../utils/googleMeetUtils';
import { isGoogleConnected } from '../utils/googleAuth';
import { createTeamsMeeting, isTeamsConnected } from '../utils/teamsUtils';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useWorkspaceRoster, useWorkspaceRosterMap, rosterParseMentions } from '../hooks/useWorkspaceRoster';
import { loadStyleProfile, buildStyleSystemPrompt, analyseWritingSample, loadLearnedPatterns } from '../services/writingMemoryService';
import { suggestPrioritySwap } from '../utils/taskUtils';
import schedulingManager from '../models/scheduling';
import useVoiceInput from '../hooks/useVoiceInput';
import VoiceInput from './AideyChat/VoiceInput';
import { detectIntent } from '../services/voiceService';
import MeetingMode from './MeetingMode';
import MeetingTranscripts from './Meetings/MeetingTranscripts';
import MeetingRecorder from './MeetingRecorder';
import { toLocalDateStr } from '../utils/calendarUtils';

/**
 * === Helper Functions ===
 */
function getTodayISO() {
  return toLocalDateStr(new Date());
}
function getTomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toLocalDateStr(d);
}

function getOrdinalSuffix(n) {
  const j = n % 10, k = n % 100;
  if (j === 1 && k !== 11) return "st";
  if (j === 2 && k !== 12) return "nd";
  if (j === 3 && k !== 13) return "rd";
  return "th";
}

// "14:00" → "2pm"  |  "14:30" → "2:30pm"  |  "09:00" → "9am"
function fmtTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const h12 = h % 12 || 12;
  const ampm = h >= 12 ? 'pm' : 'am';
  return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
}

// "2026-04-08" → "8th April"  (year appended only if ≠ current year)
function fmtDate(d) {
  if (!d) return '';
  const dt = new Date(d + 'T12:00:00');
  const day = dt.getDate();
  const month = dt.toLocaleDateString('en-GB', { month: 'long' });
  const base = `${day}${getOrdinalSuffix(day)} ${month}`;
  return dt.getFullYear() === new Date().getFullYear() ? base : `${base} ${dt.getFullYear()}`;
}

// "2pm on 8th April"
function fmtDateTime(dateStr, timeStr) {
  const d = fmtDate(dateStr), t = fmtTime(timeStr);
  if (d && t) return `${t} on ${d}`;
  return d || t || '';
}

// Builds a brief "[User confirmed: ...]" summary for chat history continuity
function buildConfirmSummary(tool, input = {}) {
  if (tool === 'send_email' || tool === 'send_email_legacy') {
    return `[User confirmed: send the email to ${input.to || 'recipient'}]`;
  }
  if (tool === 'create_invoice') {
    return `[User confirmed: create invoice for ${input.client_name || 'client'} — £${input.amount || '?'}]`;
  }
  if (tool === 'send_invoice_chase') {
    return `[User confirmed: send invoice chase to ${input.contact_name || input.to || 'client'}]`;
  }
  if (tool === 'reschedule_meeting') {
    return `[User confirmed: reschedule meeting "${input.title || 'meeting'}"]`;
  }
  if (tool === 'update_task') {
    return `[User confirmed: update task "${input.title || input.taskId || 'task'}"]`;
  }
  if (tool === 'send_overdue_task_nudges') {
    const count = Array.isArray(input.draftedNudges) ? input.draftedNudges.length : 'overdue';
    return `[User confirmed: send ${count} task nudge(s)]`;
  }
  if (tool === 'redistribute_workload') {
    const count = Array.isArray(input.approvedSuggestions) ? input.approvedSuggestions.length : 0;
    return `[User confirmed: move ${count} task(s) to balance team workload]`;
  }
  return `[User confirmed: ${tool.replace(/_/g, ' ')}]`;
}

function stripMarkdown(text) {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.*?)\*\*/g, '$1')
    .replace(/\*(.*?)\*/g, '$1')
    .replace(/`{1,3}(.*?)`{1,3}/gs, '$1')
    .replace(/^[-*•]\s+/gm, '')
    .replace(/^\d+\.\s+/gm, '');
  // [label](url) intentionally kept — renderMessage converts these to <a> elements
}

// ─── Message renderer — converts Aidey's markdown subset to React elements ────
// Handles: [label](url) links (including mailto:), and \n line breaks.
function renderMessage(text) {
  if (!text) return null;
  const cleaned = stripMarkdown(text);
  // Split into lines, then parse each line for links
  return cleaned.split('\n').map((line, li) => {
    // Tokenise the line into segments: text | link | bold
    const tokens = [];
    const pattern = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*/g;
    let last = 0, m;
    while ((m = pattern.exec(line)) !== null) {
      if (m.index > last) tokens.push({ type: 'text', value: line.slice(last, m.index) });
      if (m[1] !== undefined) {
        // Link: [label](url)
        const isMailto = m[2].startsWith('mailto:');
        tokens.push({ type: 'link', label: m[1], href: m[2], isMailto });
      } else {
        // Bold: **text**
        tokens.push({ type: 'bold', value: m[3] });
      }
      last = m.index + m[0].length;
    }
    if (last < line.length) tokens.push({ type: 'text', value: line.slice(last) });

    const rendered = tokens.map((tok, ti) => {
      if (tok.type === 'bold') return <strong key={ti}>{tok.value}</strong>;
      if (tok.type === 'link') {
        const linkStyle = {
          color: tok.isMailto ? '#0C2D48' : '#1a6db5',
          textDecoration: 'underline',
          wordBreak: 'break-all',
        };
        return (
          <a key={ti} href={tok.href} target={tok.isMailto ? '_self' : '_blank'} rel="noopener noreferrer" style={linkStyle}>
            {tok.label}
          </a>
        );
      }
      return tok.value;
    });

    return (
      <span key={li}>
        {rendered}
        {li < text.split('\n').length - 1 && <br />}
      </span>
    );
  });
}

// ─── Meeting Notes Parser ─────────────────────────────────────────────────────
// Extracts action items from free-form meeting notes.
// Supports name, @mention, department ("the marketing team will…"), and job title assignment.
function parseMeetingNotes(text, allUsers) {
  const items = [];
  const lines = text.split(/\n|;/);

  // Match a user by name, @handle, job title, or email prefix
  const findUser = (hint) => {
    if (!hint) return null;
    const q = hint.trim().replace(/^@/, '').toLowerCase();
    return allUsers.find(u =>
      u.name.toLowerCase().startsWith(q) ||
      u.name.toLowerCase().includes(q) ||
      (u.email && u.email.toLowerCase().startsWith(q)) ||
      (u.jobTitle && u.jobTitle.toLowerCase().includes(q))
    ) || null;
  };

  // Return all users in a named department
  const findDeptUsers = (deptHint) => {
    if (!deptHint) return [];
    const q = deptHint.trim().toLowerCase();
    return allUsers.filter(u => u.department && u.department.toLowerCase().includes(q));
  };

  for (const line of lines) {
    const trimmed = line.trim().replace(/^[-*•]\s*/, "");
    if (!trimmed || trimmed.length < 4) continue;

    // Pattern 1: "the [dept] team/department will/should/to [action]"
    const p1 = trimmed.match(/^(?:the\s+)?(.+?)\s+(?:team|department|dept)\s+(?:will|should|to|needs? to|must|has to)\s+(.+)/i);
    if (p1) {
      const deptUsers = findDeptUsers(p1[1]);
      if (deptUsers.length > 0) {
        items.push({ title: p1[2].trim(), assigneeDept: p1[1].trim(), assigneeIds: deptUsers.map(u => u.id), assigneeName: null, assigneeId: null });
        continue;
      }
    }

    // Pattern 2: "Name/JobTitle will/should/to/needs to action…"
    const p2 = trimmed.match(/^([A-Z][a-zÀ-ÿ]+(?:\s[A-Za-zÀ-ÿ]+)*)\s+(?:will|should|to|needs to|has to|must)\s+(.+)/i);
    if (p2) {
      const user = findUser(p2[1]);
      items.push({ title: p2[2].trim(), assigneeName: user?.name || p2[1], assigneeId: user?.id || null, assigneeIds: null });
      continue;
    }

    // Pattern 3: "@handle action"
    const p3 = trimmed.match(/^@(\w+)\s+(.+)/);
    if (p3) {
      const user = findUser(p3[1]);
      items.push({ title: p3[2].trim(), assigneeName: user?.name || p3[1], assigneeId: user?.id || null, assigneeIds: null });
      continue;
    }

    // Pattern 4: "action item: text – Name/Dept"
    const p4 = trimmed.match(/^action items?:?\s+(.+?)(?:\s*[-–(]\s*(?:assigned to\s+)?(@?\w+(?:\s\w+)?)[\s)]*)?$/i);
    if (p4) {
      const hint = p4[2];
      const user = hint ? findUser(hint) : null;
      const deptUsers = (hint && !user) ? findDeptUsers(hint) : [];
      if (deptUsers.length > 0) {
        items.push({ title: p4[1].trim(), assigneeDept: hint, assigneeIds: deptUsers.map(u => u.id), assigneeName: null, assigneeId: null });
      } else {
        items.push({ title: p4[1].trim(), assigneeName: user?.name || hint || null, assigneeId: user?.id || null, assigneeIds: null });
      }
      continue;
    }

    // Pattern 5: "[ ] task text – Name/Dept" or "task text – Name"
    const p5 = trimmed.match(/^(?:\[\s*\]\s+)?(.+?)\s*[-–]\s*@?([A-Za-z]\w+(?:\s[A-Z][a-z]+)?)$/);
    if (p5 && p5[1].length > 3) {
      const user = findUser(p5[2]);
      const deptUsers = !user ? findDeptUsers(p5[2]) : [];
      if (deptUsers.length > 0) {
        items.push({ title: p5[1].trim(), assigneeDept: p5[2], assigneeIds: deptUsers.map(u => u.id), assigneeName: null, assigneeId: null });
      } else {
        items.push({ title: p5[1].trim(), assigneeName: user?.name || p5[2], assigneeId: user?.id || null, assigneeIds: null });
      }
      continue;
    }

    // Pattern 6: bare checkbox/bullet — no assignment
    const p6 = trimmed.match(/^\[?\s*\]?\s*(.{5,})/);
    if (p6 && !/^(present|attendees|agenda|date|time|location|re:|subject):/i.test(p6[1])) {
      items.push({ title: p6[1].trim(), assigneeName: null, assigneeId: null, assigneeIds: null });
    }
  }

  return items;
}

// Recurring, Delegation, Multi-Action, Advanced Field Detection!
function parseUserInput(input, members = []) {
  // MULTI-ACTION SPLIT
  const actions = input.split(/\b(?:and|then)\b/gi).map(s => s.trim());
  if (actions.length > 1) return actions.map(parseUserInput);

  const lower = input.toLowerCase();
  const details = {};

  // ACTION DETECTION
  if (/move|reschedule/.test(lower)) details.action = "move";
  else if (/cancel|delete|remove/.test(lower)) details.action = "cancel";
  else if (/\b(write|generate|draft|compose)\b.+\b(blog|post|article|caption|copy|pr|press\s*release|social|tweet|newsletter|announcement)\b/i.test(lower) ||
    /\b(blog post|pr copy|press release|social caption|tweet|newsletter)\b/i.test(lower)) {
    details.action = "generate-content";
    const cMatch = lower.match(/\b(blog post|press release|social caption|tweet|caption|pr copy|newsletter|email draft|announcement|article)\b/i);
    details.contentType = cMatch ? cMatch[1] : "content";
  }
  else if (/\b(when am i free|find.*slot|available.*time|free.*time|what.*available|open slot|find me a time)\b/i.test(lower)) details.action = "find-slots";
  else if (/\b(what.*agenda|agenda today|on my schedule|my day|tasks today|i have \d+ tasks)\b/i.test(lower)) details.action = "agenda";
  else if (/\b(send|share)\b.+\b(booking link|meeting link|scheduling link|meeting request)\b/i.test(lower) ||
    /\b(booking link|scheduling link)\b.+\b(to|with)\b/i.test(lower)) details.action = "send-booking-link";
  else if (/\b(reply to email|draft email|send email|email to|summarize.*emails?|check.*emails?)\b/i.test(lower)) details.action = "email-command";
  else if (/\b(add contact|manage contact|find contact|show contacts|new contact)\b/i.test(lower)) details.action = "contacts";
  else if (/\b(zoom.*transcript|zoom.*recording|google meet.*transcript|meet.*transcript|teams.*transcript|recent.*meeting notes|process.*zoom|process.*meet|show.*zoom|my zoom meetings|zoom meetings|meeting transcript|meeting recordings|my.*recordings|show.*meeting notes|show my meetings|process.*meetings|recent transcripts|upload.*transcript|upload.*meeting)\b/i.test(lower)) details.action = "zoom-transcripts";
  else if (/\b(meeting notes|log.*meeting|take.*notes|action items from|meeting summary|summarise.*meeting|summarize.*meeting|start.*meeting|open.*meeting|meeting recorder)\b/i.test(lower)) details.action = "meeting-notes";
  else if (/\b(help me (write|draft|phrase|word|improve|edit|rephrase|rewrite)|improve (this|my)|make (this|it) (better|clearer|shorter|more professional|punchier)|how does this sound|edit this|fix this|review my|suggest.*subject|rephrase|rewrite this|check my writing)\b/i.test(lower)) details.action = "writing-help";
  else if (/\b(create invoice|new invoice|send invoice|add invoice|invoice.*for|draft invoice|bill.*client|raise invoice)\b/i.test(lower)) details.action = "create-invoice";
  else if (/\b(book.*hotel|book.*flight|book.*travel|travel.*to|flight.*to|arrange.*travel|plan.*trip)\b/i.test(lower)) details.action = "travel";
  else if (/\b(add|invite)\s+\S.+\s+(to|into)\s+(?:the\s+)?\S/i.test(lower) && !/\b(task|contact|note|list|team)\b/i.test(lower)) {
    details.action = "add-attendee";
    const addMatch = input.match(/\b(?:add|invite)\s+(.+?)\s+(?:to|into)\s+(?:the\s+)?(.+?)(?:\s+meeting|\s+call|\s+appointment)?\s*$/i);
    if (addMatch) {
      details.addPersonName = addMatch[1].trim();
      details.addToMeetingTitle = addMatch[2].trim();
    }
  }
  else details.action = "create";

  // Reclassify generic 'create' as 'schedule_meeting' when meeting keywords are present
  if (details.action === 'create' && (
    /\b(meeting|call|catch[- ]?up|sync|video\s+call|google\s*meet|zoom|teams|skype|appointment)\b/i.test(lower) ||
    /\b(face[\s-]to[\s-]face|in[\s-]person|schedule\s+with|book\s+with)\b/i.test(lower)
  )) {
    details.action = 'schedule_meeting';
  }

  // DATE DETECTION
  let dateTextToStrip = '';
  if (lower.includes("today")) details.date = getTodayISO();
  if (lower.includes("tomorrow")) details.date = getTomorrowISO();
  const isoDateMatch = input.match(/\b(\d{4}-\d{2}-\d{2})\b/);
  if (isoDateMatch) details.date = isoDateMatch[1];

  // Natural language dates: "8th April", "April 8th", "8 April", "the 8th of April"
  if (!details.date) {
    const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
    const monthPat = MONTHS.join('|');
    // "8th April" / "8 April" / "the 8th of April"
    const nlDate1 = input.match(new RegExp(`\\b(?:the\\s+)?(\\d{1,2})(?:st|nd|rd|th)?(?:\\s+of)?\\s+(${monthPat})`, 'i'));
    // "April 8th" / "April 8"
    const nlDate2 = !nlDate1 && input.match(new RegExp(`\\b(${monthPat})\\s+(\\d{1,2})(?:st|nd|rd|th)?\\b`, 'i'));
    if (nlDate1 || nlDate2) {
      let day, monthName;
      if (nlDate1) { day = parseInt(nlDate1[1]); monthName = nlDate1[2].toLowerCase(); }
      else { monthName = nlDate2[1].toLowerCase(); day = parseInt(nlDate2[2]); }
      const monthIdx = MONTHS.indexOf(monthName);
      if (monthIdx !== -1 && day >= 1 && day <= 31) {
        const now = new Date();
        let year = now.getFullYear();
        const candidate = new Date(year, monthIdx, day);
        if (candidate < now && candidate.toDateString() !== now.toDateString()) year++;
        details.date = `${year}-${String(monthIdx + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        dateTextToStrip = (nlDate1 || nlDate2)[0];
      }
    }
  }

  // Weekday names: "next Monday", "this Friday", "on Wednesday"
  if (!details.date) {
    const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayMatch = input.match(/\b(?:next|this)?\s*(sunday|monday|tuesday|wednesday|thursday|friday|saturday)\b/i);
    if (dayMatch) {
      const targetDay = DAYS.indexOf(dayMatch[1].toLowerCase());
      const now = new Date();
      let diff = targetDay - now.getDay();
      if (diff <= 0) diff += 7;
      const d = new Date(now);
      d.setDate(d.getDate() + diff);
      details.date = toLocalDateStr(d);
    }
  }

  // DURATION DETECTION — must come before time detection to avoid false positives
  const durationMatch = input.match(/\b(\d+(?:\.\d+)?)\s*(hours?|hrs?|minutes?|mins?)\b/i);
  if (durationMatch) {
    const val = parseFloat(durationMatch[1]);
    const unit = durationMatch[2].toLowerCase();
    details.durationMinutes = /^h/.test(unit) ? Math.round(val * 60) : Math.round(val);
  }

  // TIME DETECTION — strip duration token and natural date text first to avoid false positives
  let inputForTime = durationMatch ? input.replace(durationMatch[0], '') : input;
  if (dateTextToStrip) inputForTime = inputForTime.replace(dateTextToStrip, '');
  const timeMatch = inputForTime.match(/(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?/i);
  if (timeMatch) {
    let hours = parseInt(timeMatch[1], 10);
    let minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
    if (/pm/i.test(timeMatch[3]) && hours < 12) hours += 12;
    if (/am/i.test(timeMatch[3]) && hours === 12) hours = 0;

    // Assume PM for ambiguous times 6–11 unless am/pm specified
    if (!timeMatch[3] && hours >= 6 && hours <= 11) {
      hours += 12;
    }

    details.time = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  }

  // RECURRING
  const recurDay = input.match(/every\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i);
  if (recurDay) {
    details.recurrence = { type: "weekly", day: recurDay[1].toLowerCase() };
  }
  const recurMonth = input.match(/every\s+(\d+)(st|nd|rd|th)\s*of\s*the\s*month/i);
  if (recurMonth) {
    details.recurrence = { type: "monthly", date: parseInt(recurMonth[1]) };
  }

  // TASK TYPE & "WITH" PERSON - Enhanced title parsing
  const typeMatch = input.match(/(meeting|call|appointment|event|task|zoom|meet)/i);
  const withMatch = input.match(/with\s+([A-Za-z\s]+?)(?:\s+(?:today|tomorrow|on|at|\d))/i);

  // Check for specific meeting platform requests
  const isZoomRequest = /zoom/i.test(input);
  const isGoogleMeetRequest = /google\s*meet/i.test(input) && !/zoom/i.test(input); // ✅ More specific - require "google meet" not just "meet"

  if (typeMatch && withMatch) {
    details.title = `${typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1)} with ${withMatch[1].trim()}`;
    details.taskType = typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1);
    if (isZoomRequest) {
      details.meetingType = "Zoom";
    } else if (isGoogleMeetRequest) {
      details.meetingType = "Google Meet";
    }
  } else if (typeMatch) {
    // For standalone task types, create a more descriptive title
    const taskType = typeMatch[1][0].toUpperCase() + typeMatch[1].slice(1);
    details.taskType = taskType;
    if (isZoomRequest) {
      details.meetingType = "Zoom";
      details.taskType = "Meeting";
    } else if (isGoogleMeetRequest) {
      details.meetingType = "Google Meet";
      details.taskType = "Meeting";
    }
    // Try to extract more context for the title
    if (withMatch) {
      details.title = `${taskType} with ${withMatch[1].trim()}`;
    } else {
      details.title = taskType;
    }
  } else if (withMatch) {
    details.title = `Meeting with ${withMatch[1].trim()}`;
    details.taskType = "Meeting";
    if (isZoomRequest) {
      details.meetingType = "Zoom";
    } else if (isGoogleMeetRequest) {
      details.meetingType = "Google Meet";
    }
  } else if (isZoomRequest) {
    details.title = details.title || "Zoom Meeting";
    details.taskType = "Meeting";
    details.meetingType = "Zoom";
  } else if (isGoogleMeetRequest) {
    details.title = details.title || "Google Meet";
    details.taskType = "Meeting";
    details.meetingType = "Google Meet";
  }

  // PRIORITY
  if (/high[- ]?priority/i.test(lower)) details.priority = "High";
  else if (/low[- ]?priority/i.test(lower)) details.priority = "Low";
  else if (/medium[- ]?priority/i.test(lower)) details.priority = "Medium";

  // REMINDER (minutes before)
  const reminderMatch = input.match(/remind(?:er)?\s*(?:me)?\s*(\d+)\s*(minutes?|hours?)\s*before/i);
  if (reminderMatch) {
    const n = parseInt(reminderMatch[1]);
    details.reminderOffset = /hour/.test(reminderMatch[2]) ? n * 60 : n;
  }

  // ASSIGNEE/DELEGATION & @MENTIONS
  const assignMatch = input.match(/(?:assign|delegate)\s+(?:this\s+)?(?:to\s+)?([A-Za-z\s]+)/i);
  if (assignMatch) {
    details.assignedTo = assignMatch[1].trim();
  }

  // @MENTION PARSING — resolve against the active-workspace roster.
  const mentions = rosterParseMentions(members, input);
  if (mentions.length > 0) {
    details.mentions = mentions;
    details.assignedTo = mentions.map(m => m.id); // Store as array of user IDs
    details.attendees = mentions.map(m => m.id); // Also add as attendees for meetings

    // If it's a meeting with mentions, enhance the title
    if (details.taskType === "Meeting" || /meeting|call|appointment/i.test(input)) {
      const mentionNames = mentions.map(m => m.name).join(", ");
      if (!details.title || details.title === "Meeting") {
        details.title = `Meeting with ${mentionNames}`;
      }
    }
  }

  // TASK DETAILS
  const detailMatch = input.match(/details?\s*:\s*([\w\s.,'-]+)/i);
  if (detailMatch) details.details = detailMatch[1].trim();

  // LOCATION DETECTION — must start with a letter so "at 2pm" is never captured as a location
  const locationMatch = input.match(/\bat\s+(?!\d)([A-Za-z][A-Za-z0-9\s.,'-]{1,50}?)(?=\s+(?:today|tomorrow|\bon\s|\d{1,2}(?::\d{2})?\s*(?:am|pm))|,|$)/i);
  if (locationMatch) {
    const locText = locationMatch[1].trim();
    // Also reject if the text is just a meeting-mode phrase
    if (!/^(?:in\s*person|online|virtual|remote|the\s+office)$/i.test(locText)) {
      details.location = locText;
    }
  }

  // MEETING MODE DETECTION
  if (/online|virtual|remote|zoom|google\s*meet|teams|video/i.test(lower)) {
    details.meetingMode = "online";
    if (/zoom/i.test(lower)) details.meetingType = "Zoom";
    else if (/google\s*meet/i.test(lower) && !/zoom/i.test(lower)) details.meetingType = "Google Meet"; // ✅ More specific
    else if (/teams/i.test(lower)) details.meetingType = "Teams";
  } else if (/in\s*person|office|room|address|conference\s*room/i.test(lower)) {
    details.meetingMode = "in person";
  }

  // CATCH ALL: Better fallback for quick tasks - don't split on "at" if it's part of time
  if (!details.title) {
    let cleanTitle = input
      // Strip action verbs at the start
      .replace(/^(schedule|book|add|create|set\s+up|remind\s+me\s+to|log|record|note)\s+(a|an|the)?\s*/i, '')
      // Strip duration patterns
      .replace(/\b\d+(?:\.\d+)?\s*(?:hours?|hrs?|minutes?|mins?)\b/gi, '')
      // Strip date/time patterns
      .replace(/\b(?:today|tomorrow)\b/gi, '')
      .replace(/\b\d{4}-\d{2}-\d{2}\b/g, '')
      .replace(/\b(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?\b/gi, '')
      // Strip trailing filler
      .replace(/\b(on|at|for|in)\s*$/gi, '')
      .replace(/\s+/g, ' ')
      .trim();
    details.title = cleanTitle || "New Task";
  }

  details.rawInput = input;
  return details;
}

// Enhanced conflict check that includes all calendar sources
function findConflict(details, schedule, googleEvents = [], icalEvents = [], outlookEvents = []) {
  if (!details.date || !details.time) return null;

  const start = new Date(`${details.date}T${details.time}`);
  const durationMs = (details.durationMinutes || 60) * 60 * 1000;
  const end = new Date(start.getTime() + durationMs);

  // Check against Aidey tasks
  const aideyConflict = schedule.find(t => {
    if (!t.date || !t.time) return false;
    const tStart = new Date(`${t.date}T${t.time}`);
    const tEnd = new Date(t.deadline || tStart.getTime() + 60 * 60 * 1000);
    return t.date === details.date && (start < tEnd && end > tStart);
  });

  if (aideyConflict) return aideyConflict;

  // Check against Google Calendar events
  const googleConflict = googleEvents.find(event => {
    if (!event.date || !event.time) return false;
    const eventStart = new Date(`${event.date}T${event.time}`);
    const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000); // Assume 1 hour if no end time
    return event.date === details.date && (start < eventEnd && end > eventStart);
  });

  if (googleConflict) return googleConflict;

  // Check against iCal events
  const icalConflict = icalEvents.find(event => {
    if (!event.date || !event.time) return false;
    const eventStart = new Date(`${event.date}T${event.time}`);
    const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
    return event.date === details.date && (start < eventEnd && end > eventStart);
  });

  if (icalConflict) return icalConflict;

  // Check against Outlook events
  const outlookConflict = outlookEvents.find(event => {
    if (!event.date || !event.time) return false;
    const eventStart = new Date(`${event.date}T${event.time}`);
    const eventEnd = new Date(eventStart.getTime() + 60 * 60 * 1000);
    return event.date === details.date && (start < eventEnd && end > eventStart);
  });

  return outlookConflict || null;
}

// Returns { time, date, dayOffset } or null. Searches up to 3 days ahead within 07:00–21:00.
function findNextAvailable(details, schedule, durationMinutes = 60) {
  if (!details.date || !details.time) return null;
  const duration = (durationMinutes || 60) * 60 * 1000;
  const startDate = new Date(`${details.date}T${details.time}`);

  for (let dayOffset = 0; dayOffset <= 3; dayOffset++) {
    const baseDate = new Date(startDate);
    baseDate.setDate(baseDate.getDate() + dayOffset);

    // Same day: start searching 30 min after the requested slot; subsequent days: start at 07:00
    let slotStart = new Date(baseDate);
    if (dayOffset === 0) {
      slotStart = new Date(startDate.getTime() + 30 * 60 * 1000);
    } else {
      slotStart.setHours(7, 0, 0, 0);
    }

    const dayLimit = new Date(baseDate);
    dayLimit.setHours(21, 0, 0, 0);

    while (new Date(slotStart.getTime() + duration) <= dayLimit) {
      const slotEnd = new Date(slotStart.getTime() + duration);
      const checkStart = slotStart;
      const checkEnd = slotEnd;
      const hasConflict = schedule.some(t => {
        if (!t.date || !t.time) return false;
        const tStart = t.datetime ? new Date(t.datetime) : new Date(`${t.date}T${t.time}`);
        const tEnd = t.deadline ? new Date(t.deadline) : new Date(tStart.getTime() + 60 * 60 * 1000);
        return checkStart < tEnd && checkEnd > tStart;
      });
      if (!hasConflict) {
        return {
          time: slotStart.toTimeString().slice(0, 5),
          date: toLocalDateStr(slotStart),
          dayOffset,
        };
      }
      slotStart = new Date(slotStart.getTime() + 30 * 60 * 1000);
    }
  }
  return null;
}

// ─── AI chat helper ───────────────────────────────────────────────────────────
// Routes all AI calls through the backend (key never exposed to the browser).
// The local rule-based parser in parseUserInput is the offline backup —
// it handles all structured commands (tasks, meetings, etc.) without any network.
// Returns the parsed backend payload, or null if the backend is unreachable.
async function callAideyAI(messages, options = {}) {
  try {
    const res = await api('/api/aidey', {
      method: 'POST',
      body: JSON.stringify({ messages, ...options }),
    });
    if (res && res.ok) {
      return res.json();
    }
    const errData = await res.json().catch(() => ({}));
    const errMsg = errData?.error || '';
    console.warn('[Aidey] Backend error', res?.status, errMsg);
    // Bubble up credit / quota errors so the user sees a real message
    if (/credit|balance|quota|billing|payment/i.test(errMsg)) {
      return { __creditsError: true };
    }
  } catch (err) {
    console.warn('[Aidey] Backend unreachable:', err?.message);
  }
  return null;
}

async function confirmAideyTool(tool, input) {
  const res = await api('/api/aidey/confirm', {
    method: 'POST',
    body: JSON.stringify({ tool, input }),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data?.error || 'Failed to confirm action');
  }
  return res.json();
}

function formatConfirmedActionSummary(tool, result) {
  const payload = result?.result || result || {};
  if (tool === 'send_email') return 'Done — email sent.';
  if (tool === 'send_invoice_chase') {
    const inv = payload.invoice;
    if (inv?.number) return `Done. Chase email sent for invoice ${inv.number}. I've added a 5-day follow-up reminder too.`;
    return 'Done. Invoice chase sent and follow-up reminder set.';
  }
  if (tool === 'reschedule_overdue_tasks_plan' && Array.isArray(payload.plan)) {
    return `Done. I rescheduled ${payload.plan.length} overdue task${payload.plan.length === 1 ? '' : 's'} across the next 3 days.`;
  }
  if (tool === 'send_overdue_task_nudges') {
    return `Done. Sent ${payload.nudgesSent || 0} friendly nudge${payload.nudgesSent === 1 ? '' : 's'} to your team.`;
  }
  if (tool === 'create_task') {
    let msg = payload.task?.title ? `Done — "${payload.task.title}" is added to your tasks.` : 'Task created.';
    if (payload.joinUrl) msg += ` Zoom link: ${payload.joinUrl}`;
    else if (payload.zoomLinkFailed) msg += ' (Zoom meeting could not be created — check your Zoom connection in Settings → Video meetings.)';
    return msg;
  }
  if (tool === 'update_task') {
    if (payload.delegated) return `Done — task delegated successfully.`;
    return payload.task?.title ? `Done — "${payload.task.title}" updated.` : 'Task updated.';
  }
  if (tool === 'log_expense') {
    const e = payload.expense;
    return e ? `Done — logged £${e.amount} for "${e.title}" under ${e.category}.` : 'Expense logged.';
  }
  if (tool === 'create_project') {
    const p = payload.project;
    return p ? `Done — project "${p.name}" created${p.client ? ` for ${p.client}` : ''}.` : 'Project created.';
  }
  if (tool === 'update_project_status') {
    const p = payload.project;
    return p ? `Done — "${p.name}" is now marked as ${p.status}.` : 'Project updated.';
  }
  if (tool === 'redistribute_workload') {
    const moved = payload.moved ?? 0;
    return moved === 0 ? 'No tasks were moved.' : `Done — moved ${moved} task${moved === 1 ? '' : 's'} to balance the team's workload.`;
  }
  return `Done.`;
}

function isLikelyStructuredCommand(input) {
  return /\b(create|add|schedule|book|move|reschedule|cancel|delete|send|email|invoice|note|contact|assign|delegate|task|meeting|overdue|capacity|availability|travel|follow[-\s]?up|nudge|transcrib|expense|log|project|status|summary|financial|revenue|budget|team|workload|complete|done|finish|mark)\b/i.test(input || '');
}

// Fire DOM events so context providers refresh their data without a page reload.
function fireToolEvents(toolNames) {
  if (!Array.isArray(toolNames) || !toolNames.length) return;
  const TASK_TOOLS = new Set(['create_task', 'update_task', 'reschedule_meeting', 'block_travel_time', 'reschedule_overdue_tasks_plan', 'send_invoice_chase']);
  if (toolNames.some(t => TASK_TOOLS.has(t))) window.dispatchEvent(new Event('aidey:tasksUpdated'));
  if (toolNames.includes('create_note')) window.dispatchEvent(new Event('aidey:notesUpdated'));
  if (toolNames.includes('add_contact')) window.dispatchEvent(new Event('aidey:contactsUpdated'));
  if (toolNames.includes('create_invoice')) window.dispatchEvent(new Event('aidey:invoicesUpdated'));
  if (toolNames.includes('log_expense')) window.dispatchEvent(new Event('aidey:expensesUpdated'));
  if (toolNames.some(t => ['create_project', 'update_project_status'].includes(t))) window.dispatchEvent(new Event('aidey:projectsUpdated'));
  if (toolNames.includes('open_email_inbox')) window.dispatchEvent(new Event('aidey:openEmailInbox'));
}

// ─── Approval Card ────────────────────────────────────────────────────────────
// Rendered inline in the chat message list for any action that requires user
// confirmation before the backend executes it.

const APPROVAL_CARD_META = {
  send_email: { icon: '📧', title: 'Send Email' },
  send_email_legacy: { icon: '📧', title: 'Send Email' },
  send_invoice_chase: { icon: '🧾', title: 'Send Invoice Chase' },
  reschedule_meeting: { icon: '📅', title: 'Reschedule Meeting' },
  reschedule_overdue_tasks_plan: { icon: '🗂️', title: 'Reschedule Overdue Tasks' },
  send_overdue_task_nudges: { icon: '📣', title: 'Send Team Nudges' },
  create_task: { icon: '✅', title: 'Create Task' },
  create_invoice: { icon: '📄', title: 'Create Invoice' },
  update_task: { icon: '✏️', title: 'Update Task' },
  add_contact: { icon: '👤', title: 'Add Contact' },
  block_travel_time: { icon: '🚗', title: 'Block Travel Time' },
  log_expense: { icon: '🧾', title: 'Log Expense' },
  create_project: { icon: '📁', title: 'Create Project' },
  update_project_status: { icon: '📁', title: 'Update Project' },
};

function ApprovalCard({ approvalData, onConfirm, onCancel }) {
  const [editMode, setEditMode] = React.useState(false);
  const [editedBody, setEditedBody] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const { tool, input = {}, confirmationPrompt } = approvalData;
  const meta = APPROVAL_CARD_META[tool] || { icon: '⚡', title: tool.replace(/_/g, ' ') };

  // Build summary rows per tool
  const summaryRows = [];
  if (tool === 'send_email' || tool === 'send_email_legacy') {
    if (input.to) summaryRows.push(['To', input.to]);
    if (input.subject) summaryRows.push(['Subject', input.subject]);
  } else if (tool === 'send_invoice_chase') {
    if (input.invoice_number) summaryRows.push(['Invoice', input.invoice_number]);
    if (input.invoice_contact_name) summaryRows.push(['Client', input.invoice_contact_name]);
    if (input.draft_subject) summaryRows.push(['Subject', input.draft_subject]);
  } else if (tool === 'reschedule_meeting') {
    if (input.title_match) summaryRows.push(['Meeting', input.title_match]);
    if (input.new_datetime) {
      const dt = new Date(input.new_datetime);
      if (!isNaN(dt.getTime())) {
        summaryRows.push(['New time', dt.toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })]);
      }
    }
  } else if (tool === 'create_task') {
    if (input.title) summaryRows.push(['Task', input.title]);
    if (input.due_date || input.date) summaryRows.push(['Due', input.due_date || input.date]);
    if (input.priority) summaryRows.push(['Priority', input.priority]);
  } else if (tool === 'create_invoice') {
    if (input.client_name || input.contactName) summaryRows.push(['Client', input.client_name || input.contactName]);
    if (input.amount) summaryRows.push(['Amount', `£${input.amount}`]);
    if (input.due_date || input.dueDate) summaryRows.push(['Due', input.due_date || input.dueDate]);
  } else if (tool === 'update_task') {
    if (input.title_match || input.title) summaryRows.push(['Task', input.title_match || input.title]);
    if (input.due_date || input.date) summaryRows.push(['New Date', input.due_date || input.date]);
  }

  const hasDraftBody = ['send_email', 'send_email_legacy', 'send_invoice_chase'].includes(tool);
  const draftBody = input.draft_body || input.body || '';

  async function handleConfirmClick() {
    if (busy) return;
    setBusy(true);
    await onConfirm(editMode ? editedBody : undefined);
    // onConfirm replaces this card with a result message; no need to reset busy
  }

  function handleEditStart() {
    setEditedBody(draftBody);
    setEditMode(true);
  }

  const BTN = {
    border: 'none', borderRadius: 7, padding: '0.44em 1.15em',
    fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif", lineHeight: 1.4, transition: 'opacity 0.15s',
  };

  // Strip the "Reply yes to..." tail from confirmationPrompt for the preview pane
  const planPreview = !hasDraftBody && confirmationPrompt
    ? confirmationPrompt.replace(/\n?Reply [""]?yes[""]? to[\s\S]*$/i, '').trim()
    : '';

  return (
    <div style={{
      border: '2px solid #d4a827',
      borderRadius: 14,
      background: '#fffdf0',
      padding: '1rem 1.1rem',
      boxShadow: '0 2px 12px rgba(212,168,39,0.14)',
      fontFamily: "'Raleway', sans-serif",
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.7rem' }}>
        <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>{meta.icon}</span>
        <strong style={{ fontSize: '0.97rem', color: '#2c2c2c' }}>{meta.title}</strong>
        <span style={{
          marginLeft: 'auto', fontSize: '0.68rem', background: '#d4a827', color: '#fff',
          borderRadius: 99, padding: '0.12em 0.6em', fontWeight: 700, letterSpacing: '0.03em',
        }}>Action required</span>
      </div>

      {/* Summary rows */}
      {summaryRows.length > 0 && (
        <div style={{
          background: '#fff8e1', borderRadius: 8, padding: '0.48rem 0.7rem',
          marginBottom: '0.65rem', fontSize: '0.83rem',
        }}>
          {summaryRows.map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.15rem' }}>
              <span style={{ color: '#a07018', minWidth: 68, fontWeight: 700 }}>{label}:</span>
              <span style={{ color: '#333', wordBreak: 'break-all' }}>{value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Plan preview (for reschedule plans, nudge lists, etc.) */}
      {planPreview && (
        <div style={{
          background: '#f7f7f5', borderRadius: 8, padding: '0.5rem 0.7rem',
          marginBottom: '0.65rem', fontSize: '0.81rem', color: '#555',
          maxHeight: 150, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.55,
        }}>
          {planPreview}
        </div>
      )}

      {/* Draft body preview / inline edit area */}
      {hasDraftBody && (
        <div style={{ marginBottom: '0.7rem' }}>
          {editMode ? (
            <textarea
              value={editedBody}
              onChange={e => setEditedBody(e.target.value)}
              style={{
                width: '100%', minHeight: 120, maxHeight: 240,
                border: '1.5px solid #d4a827', borderRadius: 8,
                padding: '0.5rem 0.65rem', fontSize: '0.83rem',
                fontFamily: "'Raleway', sans-serif", resize: 'vertical',
                background: '#fffef6', color: '#2c2c2c', boxSizing: 'border-box',
              }}
            />
          ) : (
            <div style={{
              background: '#f9f9f6', borderRadius: 8, border: '1px solid #e8e0c0',
              padding: '0.55rem 0.7rem', fontSize: '0.81rem', color: '#444',
              maxHeight: 170, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.55,
            }}>
              {draftBody || '(no preview available)'}
            </div>
          )}
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          disabled={busy}
          onClick={handleConfirmClick}
          style={{ ...BTN, background: busy ? '#c9a82c' : '#d4a827', color: '#fff', opacity: busy ? 0.7 : 1 }}
        >
          {editMode ? 'Send Edited Version' : (busy ? 'Working…' : 'Confirm')}
        </button>
        {!editMode && hasDraftBody && (
          <button type="button" onClick={handleEditStart}
            style={{ ...BTN, background: 'transparent', color: '#7a5a10', border: '1.5px solid #d4a827' }}>
            Edit
          </button>
        )}
        {editMode && (
          <button type="button" onClick={() => setEditMode(false)}
            style={{ ...BTN, background: 'transparent', color: '#888', border: '1px solid #ddd' }}>
            Back
          </button>
        )}
        <button type="button" onClick={onCancel} disabled={busy}
          style={{ ...BTN, background: 'transparent', color: '#999', border: '1px solid #e0e0e0', marginLeft: 'auto' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function EmailDraftCard({ draft, onSend, onCancel }) {
  const [editMode, setEditMode] = React.useState(false);
  const [editedBody, setEditedBody] = React.useState(draft.body || '');
  const [busy, setBusy] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const BTN = {
    border: 'none', borderRadius: 7, padding: '0.44em 1.15em',
    fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif", lineHeight: 1.4, transition: 'opacity 0.15s',
  };

  async function handleSend() {
    if (busy || done) return;
    setBusy(true);
    await onSend(editMode ? editedBody : draft.body);
    setDone(true);
  }

  return (
    <div style={{
      border: '2px solid #0C2D48', borderRadius: 14, background: '#f0f4f8',
      padding: '1rem 1.1rem', fontFamily: "'Raleway', sans-serif",
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem', marginBottom: '0.7rem' }}>
        <span style={{ fontSize: '1.25rem', lineHeight: 1 }}>📧</span>
        <strong style={{ fontSize: '0.97rem', color: '#0C2D48' }}>Draft Email</strong>
        <span style={{
          marginLeft: 'auto', fontSize: '0.68rem', background: '#0C2D48', color: '#fff',
          borderRadius: 99, padding: '0.12em 0.6em', fontWeight: 700, letterSpacing: '0.03em',
        }}>Review before sending</span>
      </div>

      <div style={{
        background: '#fff', borderRadius: 8, padding: '0.48rem 0.7rem',
        marginBottom: '0.65rem', fontSize: '0.83rem',
      }}>
        {draft.to && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.15rem' }}>
            <span style={{ color: '#0C2D48', minWidth: 60, fontWeight: 700 }}>To:</span>
            <span style={{ color: '#333', wordBreak: 'break-all' }}>{draft.to}</span>
          </div>
        )}
        {draft.subject && (
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <span style={{ color: '#0C2D48', minWidth: 60, fontWeight: 700 }}>Subject:</span>
            <span style={{ color: '#333' }}>{draft.subject}</span>
          </div>
        )}
      </div>

      <div style={{ marginBottom: '0.7rem' }}>
        {editMode ? (
          <textarea
            value={editedBody}
            onChange={e => setEditedBody(e.target.value)}
            style={{
              width: '100%', minHeight: 120, maxHeight: 240,
              border: '1.5px solid #0C2D48', borderRadius: 8,
              padding: '0.5rem 0.65rem', fontSize: '0.83rem',
              fontFamily: "'Raleway', sans-serif", resize: 'vertical',
              background: '#fff', color: '#2c2c2c', boxSizing: 'border-box',
            }}
          />
        ) : (
          <div style={{
            background: '#f9f9f9', borderRadius: 8, border: '1px solid #e0e0e0',
            padding: '0.55rem 0.7rem', fontSize: '0.81rem', color: '#444',
            maxHeight: 170, overflowY: 'auto', whiteSpace: 'pre-wrap', lineHeight: 1.55,
          }}>
            {draft.body || '(no body)'}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <button
          type="button"
          disabled={busy || done}
          onClick={handleSend}
          style={{ ...BTN, background: '#0C2D48', color: '#fff', opacity: (busy || done) ? 0.7 : 1 }}
        >
          {busy ? 'Sending…' : done ? 'Sent' : (editMode ? 'Send edited version' : 'Send')}
        </button>
        {!editMode && !done && (
          <button type="button" onClick={() => { setEditMode(true); setEditedBody(draft.body || ''); }}
            style={{ ...BTN, background: 'transparent', color: '#0C2D48', border: '1.5px solid #0C2D48' }}>
            Edit
          </button>
        )}
        {editMode && (
          <button type="button" onClick={() => setEditMode(false)}
            style={{ ...BTN, background: 'transparent', color: '#888', border: '1px solid #ddd' }}>
            Back
          </button>
        )}
        {!done && (
          <button type="button" onClick={onCancel} disabled={busy}
            style={{ ...BTN, background: 'transparent', color: '#999', border: '1px solid #e0e0e0', marginLeft: 'auto' }}>
            Cancel
          </button>
        )}
      </div>
    </div>
  );
}

function WorkloadRedistributionCard({ suggestions, onConfirm, onCancel }) {
  const [checked, setChecked] = React.useState(() => new Set(suggestions.map((_, i) => i)));
  const [busy, setBusy] = React.useState(false);

  const allSelected = checked.size === suggestions.length;
  const toggle = (i) => setChecked(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });
  const toggleAll = () => setChecked(allSelected ? new Set() : new Set(suggestions.map((_, i) => i)));

  async function handleConfirm() {
    const approved = suggestions.filter((_, i) => checked.has(i));
    if (approved.length === 0) return;
    setBusy(true);
    await onConfirm(approved);
  }

  const CARD = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px', fontFamily: 'inherit' };
  const BTN = { border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };

  return (
    <div style={CARD}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0C2D48', marginBottom: 2 }}>Workload redistribution suggestions</div>
        <div style={{ fontSize: 11, color: '#999' }}>Review and approve which tasks to move</div>
      </div>
      <button onClick={toggleAll} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: '#5352ed', padding: '0 0 8px 0', fontWeight: 600 }}>
        {allSelected ? 'Deselect all' : 'Select all'}
      </button>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {suggestions.map((s, i) => (
          <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '7px 10px', borderRadius: 7, border: `1px solid ${checked.has(i) ? '#5352ed' : '#e0e0e0'}`, background: checked.has(i) ? '#f4f3ff' : '#fafafa' }}>
            <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 2, accentColor: '#5352ed', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#222', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.taskTitle}</div>
              <div style={{ fontSize: 11, color: '#666' }}>
                <span style={{ color: '#e05252' }}>{s.fromMember}</span>
                <span style={{ color: '#aaa' }}> ({s.fromCount} tasks) </span>
                <span style={{ color: '#aaa' }}>→ </span>
                <span style={{ color: '#27ae60' }}>{s.toMember}</span>
                <span style={{ color: '#aaa' }}> ({s.toCount} tasks)</span>
                {s.dueDate && <span style={{ color: '#aaa' }}> · due {new Date(s.dueDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}</span>}
              </div>
            </div>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleConfirm} disabled={busy || checked.size === 0} style={{ ...BTN, background: checked.size > 0 ? '#0C2D48' : '#ccc', color: '#fff', opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Moving…' : `Move ${checked.size} task${checked.size === 1 ? '' : 's'}`}
        </button>
        <button onClick={onCancel} disabled={busy} style={{ ...BTN, background: 'transparent', color: '#999', border: '1px solid #e0e0e0' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function SessionResumeCard({ sessionTitle, sessionTime, onContinue, onFresh }) {
  const CARD = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px', fontFamily: 'inherit' };
  const BTN = { border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  return (
    <div style={CARD}>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0C2D48', marginBottom: 4 }}>Welcome back!</div>
      <div style={{ fontSize: 12, color: '#555', marginBottom: 12, lineHeight: 1.5 }}>
        Last time we were working on: <strong>{sessionTitle}</strong>
        {sessionTime && <span style={{ color: '#aaa', fontWeight: 400 }}> ({sessionTime})</span>}.
        <br />Would you like to carry on, or start something new?
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={onContinue} style={{ ...BTN, background: '#0C2D48', color: '#fff' }}>Carry on</button>
        <button onClick={onFresh} style={{ ...BTN, background: 'transparent', color: '#999', border: '1px solid #e0e0e0' }}>Start fresh</button>
      </div>
    </div>
  );
}

function AbsenceDelegationCard({ absenceData, leaveRequestId, onConfirm, onCancel }) {
  const { absentMember, leaveStart, leaveEnd, atRiskTasks } = absenceData || {};
  const [selections, setSelections] = React.useState(() => {
    const init = {};
    (atRiskTasks || []).forEach((t, i) => {
      init[i] = t.suggestedAssignees?.[0]?.id || '';
    });
    return init;
  });
  const [checked, setChecked] = React.useState(() => new Set((atRiskTasks || []).map((_, i) => i)));
  const [busy, setBusy] = React.useState(false);

  const toggle = (i) => setChecked(prev => {
    const next = new Set(prev); next.has(i) ? next.delete(i) : next.add(i); return next;
  });

  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  async function handleConfirm() {
    if (checked.size === 0) return;
    setBusy(true);
    const delegations = [];
    (atRiskTasks || []).forEach((t, i) => {
      if (checked.has(i) && selections[i]) {
        delegations.push({ taskId: t.id, assigneeId: selections[i] });
      }
    });
    if (!delegations.length) { setBusy(false); return; }
    await onConfirm({ leaveRequestId, delegations });
  }

  const CARD = { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, padding: '14px 16px', fontFamily: 'inherit' };
  const BTN = { border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
  const leaveRange = leaveStart && leaveEnd ? ` (${fmtDate(leaveStart)} – ${fmtDate(leaveEnd)})` : '';

  return (
    <div style={CARD}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0C2D48', marginBottom: 2 }}>
          Task coverage — {absentMember?.name} on leave{leaveRange}
        </div>
        <div style={{ fontSize: 11, color: '#999' }}>Select tasks to delegate and choose an assignee for each</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
        {(atRiskTasks || []).map((t, i) => (
          <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: '8px 10px', borderRadius: 7, border: `1px solid ${checked.has(i) ? '#5352ed' : '#e0e0e0'}`, background: checked.has(i) ? '#f4f3ff' : '#fafafa' }}>
            <input type="checkbox" checked={checked.has(i)} onChange={() => toggle(i)} style={{ marginTop: 3, accentColor: '#5352ed', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#222', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {t.title}
                {t.dueDate && <span style={{ fontWeight: 400, color: '#aaa', marginLeft: 6 }}>· due {fmtDate(t.dueDate)}</span>}
                {t.priority && t.priority !== 'medium' && <span style={{ fontWeight: 700, color: t.priority === 'high' || t.priority === 'urgent' ? '#e05252' : '#888', marginLeft: 6, fontSize: 10, textTransform: 'uppercase' }}>{t.priority}</span>}
              </div>
              {t.projectName && <div style={{ fontSize: 10, color: '#aaa', marginBottom: 4 }}>{t.projectName}</div>}
              {checked.has(i) && t.suggestedAssignees?.length > 0 && (
                <select
                  value={selections[i] || ''}
                  onChange={e => setSelections(prev => ({ ...prev, [i]: e.target.value }))}
                  style={{ fontSize: 11, padding: '3px 6px', borderRadius: 5, border: '1px solid #ddd', background: '#fff', marginTop: 2, maxWidth: '100%' }}
                  onClick={e => e.stopPropagation()}
                >
                  <option value="">Assign to…</option>
                  {t.suggestedAssignees.map(a => (
                    <option key={a.id} value={a.id}>{a.name} ({a.currentTaskCount} task{a.currentTaskCount === 1 ? '' : 's'})</option>
                  ))}
                </select>
              )}
            </div>
          </label>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={handleConfirm} disabled={busy || checked.size === 0} style={{ ...BTN, background: checked.size > 0 ? '#0C2D48' : '#ccc', color: '#fff', opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Delegating…' : `Delegate ${checked.size} task${checked.size === 1 ? '' : 's'}`}
        </button>
        <button onClick={onCancel} disabled={busy} style={{ ...BTN, background: 'transparent', color: '#999', border: '1px solid #e0e0e0' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function aideyGreeting(isChild) {
  return isChild ? "Hi, I'm Aidey! How can I help?" : "Hi, I'm Aidey. How can I help you?";
}

function IntegrationPrompt({ integrationName, onNavigate }) {
  const MSGS = {
    google: "To create Google Meet links I need access to your Google account. Connect it under Settings → Video meetings, and it only takes a moment.",
    zoom: "To create Zoom links I need access to your Zoom account. Connect it under Settings → Video meetings, and it only takes a moment.",
    microsoft: "To create Microsoft Teams links I need access to your Microsoft account. Connect it under Settings → Video meetings, and it only takes a moment.",
  };
  const message = MSGS[integrationName] || `To use this feature, connect your ${integrationName} account under Settings → Video meetings.`;
  const BTN = {
    border: 'none', borderRadius: 7, padding: '0.44em 1.15em',
    fontSize: '0.84rem', fontWeight: 700, cursor: 'pointer',
    fontFamily: "'Raleway', sans-serif", lineHeight: 1.4,
    background: '#0C2D48', color: '#fff',
  };
  return (
    <div style={{
      border: '2px solid #0C2D48', borderRadius: 14, background: '#f0f4f8',
      padding: '1rem 1.1rem', fontFamily: "'Raleway', sans-serif",
    }}>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.92rem', color: '#2c2c2c', lineHeight: 1.5 }}>{message}</p>
      <button type="button" onClick={onNavigate} style={BTN}>Go to Video meetings</button>
    </div>
  );
}

/**
 * === Main Chat Component ===
 */
// Chooser shown when the user asks to turn a meeting into tasks: pull an existing
// transcript (MeetingTranscripts) vs record live / paste one now (MeetingRecorder).
function MeetingChoiceModal({ onTranscript, onRecord, onCancel }) {
  const card = { width: '100%', textAlign: 'left', padding: '0.85rem 1rem', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', cursor: 'pointer', marginBottom: '0.6rem', fontFamily: "'Raleway', sans-serif" };
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 202, background: 'rgba(12,45,72,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '1.4rem', maxWidth: 380, width: '100%', boxShadow: '0 12px 40px rgba(0,0,0,0.25)', fontFamily: "'Raleway', sans-serif" }}>
        <div style={{ fontWeight: 800, fontSize: '1.05rem', color: '#0C2D48', marginBottom: '0.9rem' }}>Turn a meeting into tasks</div>
        <button type="button" onClick={onTranscript} style={card}>
          <div style={{ fontWeight: 700, color: '#0C2D48', fontSize: '0.9rem' }}>📄 I have a transcript</div>
          <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 2 }}>Pull recent Zoom, Google Meet &amp; Teams recordings</div>
        </button>
        <button type="button" onClick={onRecord} style={card}>
          <div style={{ fontWeight: 700, color: '#0C2D48', fontSize: '0.9rem' }}>🎙️ Record or paste live</div>
          <div style={{ fontSize: '0.78rem', color: '#888', marginTop: 2 }}>Listen in person, or paste any transcript</div>
        </button>
        <button type="button" onClick={onCancel} style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600, marginTop: '0.3rem', fontFamily: "'Raleway', sans-serif" }}>Cancel</button>
      </div>
    </div>
  );
}

export default function SuperAideyChat({
  onCreateTask,
  onMoveTask = () => { },
  onCancelTask = () => { },
  userSchedule = [],
  googleEvents = [],
  icalEvents = [],
  outlookEvents = [],
  onClose,
  isPopup = false,
  scrollable = false,
  fullPage = false,
  sessionId: sessionIdProp = null,
  onSessionUpdated = null,
  setFilter = null,
  onCreateMeetingTask = null,
  proactiveMessage = null,
  onProactiveHandled = null,
  notificationProactive = null,
  onNotificationProactiveHandled = null,
  clashContext = null,
  onClashContextHandled = null,
  initialInput = "",
  notifContext = null,
  onOpenDocumentImporter = null,
  closeRef = null,
  projectId: projectIdProp = null,
}) {
  const { currentUser, activeWorkspaceId, workspaces } = useCurrentUser();
  const { members: rosterMembers } = useWorkspaceRoster();
  const rosterMap = useWorkspaceRosterMap();
  // Only account-holders are @mentionable / assignable from chat — a mention
  // notifies by userId, which accountless household members don't have.
  const mentionableMembers = useMemo(
    () => rosterMembers.filter(m => m.userId),
    [rosterMembers]
  );
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';
  const isChildWorkspace = activeWorkspace?.type === 'child';
  const { contacts } = useContacts();
  const { invoices, refresh: refreshInvoices } = useInvoices();
  // Stable session ID for this mount — new popup = new ID = fresh chat
  const sessionIdRef = useRef(sessionIdProp || newSessionId());

  const [chatMessages, setChatMessages] = useState(() => {
    if (sessionIdProp) {
      const session = loadSession(sessionIdProp);
      if (session?.messages?.length) return session.messages;
    }
    return [{ role: "assistant", content: aideyGreeting(isChildWorkspace), ts: Date.now(), type: 'greeting' }];
  });
  const [chatInput, setChatInput] = useState(initialInput);
  const [pendingTask, setPendingTask] = useState(null);
  const [pendingField, setPendingField] = useState(null);
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [pendingToolConfirmation, setPendingToolConfirmation] = useState(null);
  const chatScrollRef = useRef(null);

  // Correct the greeting if workspace type becomes known after initial render
  useEffect(() => {
    setChatMessages(prev => {
      if (prev.length !== 1) return prev;
      const first = prev[0];
      if (first.role !== 'assistant' || first.type !== 'greeting') return prev;
      const correct = aideyGreeting(isChildWorkspace);
      if (first.content === correct) return prev;
      return [{ ...first, content: correct }];
    });
  }, [isChildWorkspace]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch a purposeful opener when chat is triggered from a notification
  useEffect(() => {
    if (!notifContext) return;
    let cancelled = false;
    api('/api/aidey/notification-opener', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ notificationId: notifContext.notificationId, type: notifContext.type, data: notifContext.data || notifContext }),
    }).then(data => {
      if (!cancelled && data?.opener) {
        setChatMessages([{ role: 'assistant', content: data.opener, ts: Date.now() }]);
      }
    }).catch(() => {});
    return () => { cancelled = true; };
  }, [notifContext]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Voice input state ─────────────────────────────────────────────────────
  const [voiceConfirmation, setVoiceConfirmation] = useState(null); // { intent, summary, data }
  const wasVoiceInputRef = useRef(false);
  const [isMeetingTranscribing, setIsMeetingTranscribing] = useState(false);
  const [liveMeetingTranscript, setLiveMeetingTranscript] = useState('');
  const meetingRecognitionRef = useRef(null);
  const isMeetingTranscribingRef = useRef(false);
  const [pendingTranscribePrompt, setPendingTranscribePrompt] = useState(null);
  const lastProactiveIdRef = useRef(null);
  const currentMeetingTitleRef = useRef(null);

  // Meeting Mode (upgraded)
  const [meetingModeActive, setMeetingModeActive] = useState(false);
  const [meetingModeAttendees, setMeetingModeAttendees] = useState([]);
  const [meetingModeTitle, setMeetingModeTitle] = useState('');
  const [pendingMeetingAttendees, setPendingMeetingAttendees] = useState(null); // waiting for attendee names
  const [zoomPanelOpen, setZoomPanelOpen] = useState(false);
  const [recorderOpen, setRecorderOpen] = useState(false);       // live/DIY MeetingRecorder overlay
  const [meetingChoiceOpen, setMeetingChoiceOpen] = useState(false); // "transcript vs record" chooser

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

  const { voiceState, permissionState, startListening, requestPermission } =
    useVoiceInput({
      onTranscript: (text) => {
        setChatInput(text);
        wasVoiceInputRef.current = true;
      },
      onInterimTranscript: (text) => {
        setChatInput(text);
      },
    });

  function startMeetingTranscription() {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      addMessage('assistant', 'Live transcription is not supported in this browser.');
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let merged = '';
      for (let i = 0; i < event.results.length; i += 1) {
        merged += `${event.results[i][0].transcript} `;
      }
      setLiveMeetingTranscript(merged.trim());
    };

    recognition.onerror = (event) => {
      addMessage('assistant', `Transcription error: ${event.error || 'unknown error'}`);
      setIsMeetingTranscribing(false);
    };

    recognition.onend = () => {
      if (isMeetingTranscribingRef.current) {
        try { recognition.start(); } catch (_) { }
      }
    };

    meetingRecognitionRef.current = recognition;
    isMeetingTranscribingRef.current = true;
    setLiveMeetingTranscript('');
    setIsMeetingTranscribing(true);
    addMessage('assistant', 'Transcribing now. Say "stop transcribing" or click Stop to finish.');
    recognition.start();
  }

  function processMeetingTranscriptAndAskApproval(transcriptText) {
    const allUsers = mentionableMembers;
    const actionItems = parseMeetingNotes(transcriptText, allUsers);

    const now = new Date();
    const relevantMeeting = [...userSchedule]
      .filter((t) => /meeting|call|appointment/i.test(t.taskType || t.title || ''))
      .map((t) => ({ ...t, _at: new Date(t.datetime || `${t.date}T${t.time || '09:00:00'}`) }))
      .filter((t) => !Number.isNaN(t._at.getTime()))
      .sort((a, b) => Math.abs(a._at - now) - Math.abs(b._at - now))[0];

    const noteTitle = currentMeetingTitleRef.current
      ? `Meeting transcript: ${currentMeetingTitleRef.current}`
      : relevantMeeting
        ? `Meeting transcript: ${relevantMeeting.title}`
        : `Meeting transcript ${now.toISOString().slice(0, 10)}`;
    currentMeetingTitleRef.current = null;
    const linkedMeta = relevantMeeting
      ? `\n\n[Linked Meeting]\nID: ${relevantMeeting.id || 'n/a'}\nTitle: ${relevantMeeting.title || 'Meeting'}\nDate: ${relevantMeeting.date || relevantMeeting._at?.toISOString().slice(0, 10) || 'n/a'}\nTime: ${relevantMeeting.time || relevantMeeting._at?.toTimeString().slice(0, 5) || 'n/a'}`
      : '';

    api('/api/notes', {
      method: 'POST',
      body: JSON.stringify({
        id: String(Date.now()),
        title: noteTitle,
        text: `${transcriptText}${linkedMeta}`,
        blocks: [],
      }),
    }).catch((err) => console.error('Failed to save transcript note:', err));

    if (!actionItems.length) {
      addMessage('assistant', 'I captured the transcript and saved it as a note, but I could not extract clear action items.');
      return;
    }

    const preview = actionItems.slice(0, 8).map((item) => `• ${item.title}${item.assigneeName ? ` — ${item.assigneeName}` : ''}`).join('\n');
    addMessage('assistant', `I extracted ${actionItems.length} action item(s):\n\n${preview}\n\nReply **approve** to create these tasks or **cancel**.`);
    setPendingTask({
      ...(pendingTask || {}),
      transcript: transcriptText,
      transcriptActionItems: actionItems,
    });
    setPendingField('meetingTranscriptReview');
  }

  function stopMeetingTranscription() {
    setIsMeetingTranscribing(false);
    isMeetingTranscribingRef.current = false;
    try {
      meetingRecognitionRef.current?.stop();
    } catch (_) { }
    const transcript = (liveMeetingTranscript || '').trim();
    if (!transcript) {
      addMessage('assistant', 'No transcript captured yet.');
      return;
    }
    processMeetingTranscriptAndAskApproval(transcript);
  }

  function activateMeetingMode(attendees, title) {
    setMeetingModeAttendees(attendees || []);
    setMeetingModeTitle(title || '');
    setMeetingModeActive(true);
    setPendingTranscribePrompt(null);
    setPendingMeetingAttendees(null);
  }

  function handleMeetingEnd({ transcript, postMeeting, tasksCreated, meetingTitle, draftEmail }) {
    setMeetingModeActive(false);
    setMeetingModeAttendees([]);
    setMeetingModeTitle('');

    if (draftEmail) {
      const cardId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
      setChatMessages(prev => [
        ...prev,
        { role: 'assistant', content: `Meeting complete. I've drafted a summary email ready to review.`, ts: Date.now() },
        { role: 'email_draft', emailDraft: draftEmail, cardId, ts: Date.now() + 1 },
      ]);
      return;
    }

    const msg = postMeeting
      ? `Meeting complete. ${tasksCreated ? `Created ${tasksCreated} task${tasksCreated === 1 ? '' : 's'}.` : ''} Notes saved.`
      : 'Meeting mode closed.';
    addMessage('assistant', msg);

    if (transcript) {
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      window.dispatchEvent(new Event('aidey:notesUpdated'));
    }
  }

  function handleMeetingDismiss() {
    setMeetingModeActive(false);
    setMeetingModeAttendees([]);
    setMeetingModeTitle('');
    addMessage('assistant', 'Meeting mode closed.');
  }

  // Consume proactiveMessage from parent (App.js) when it changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!proactiveMessage || proactiveMessage.id === lastProactiveIdRef.current) return;
    lastProactiveIdRef.current = proactiveMessage.id;
    onProactiveHandled?.();
    // INTRO-2: a first-use chat intro carries kind:'intro' + a ready body. Render it
    // as a plain assistant message and stop (no meeting-notes handling).
    if (proactiveMessage.kind === 'intro') {
      if (proactiveMessage.body) addMessage('assistant', proactiveMessage.body);
      return;
    }
    const attendees = Array.isArray(proactiveMessage.attendees) ? proactiveMessage.attendees : [];
    const names = attendees.map(a => a.name || a).filter(Boolean).join(', ');
    const withLine = names ? ` with ${names}` : '';
    const mins = proactiveMessage.minsUntil;
    const minsText = mins ? `in ${mins} min${mins === 1 ? '' : 's'}` : 'in a few minutes';
    addMessage('assistant', `Your meeting **"${proactiveMessage.title}"**${withLine} starts ${minsText}. Want me to take notes?`);
    setPendingTranscribePrompt({ title: proactiveMessage.title, attendees });
  }, [proactiveMessage]); // eslint-disable-line

  // Deliver notification-routed message as an Aidey message
  const lastNotifProactiveIdRef = useRef(null);
  useEffect(() => {
    if (!notificationProactive || notificationProactive.id === lastNotifProactiveIdRef.current) return;
    lastNotifProactiveIdRef.current = notificationProactive.id;
    onNotificationProactiveHandled?.();
    addMessage('assistant', notificationProactive.message);
  }, [notificationProactive]); // eslint-disable-line

  // Pre-fill chat input when opened from a scheduling conflict ("Ask Aidey")
  useEffect(() => {
    if (!clashContext) return;
    setChatInput(clashContext);
    onClashContextHandled?.();
  }, [clashContext]); // eslint-disable-line

  useEffect(() => {
    let ignore = false;
    const fetchProactive = async () => {
      try {
        const res = await api('/api/aidey/proactive');
        if (!res.ok || ignore) return;
        const data = await res.json();
        if (!ignore && data?.nudge?.message) {
          setChatMessages((prev) => {
            const hasExisting = prev.some((m) => m.role === 'assistant' && m.meta?.nudgeType === data.nudge.type);
            if (hasExisting) return prev;
            return [...prev, { role: 'assistant', content: data.nudge.message, ts: Date.now(), meta: { nudgeType: data.nudge.type } }];
          });
        }
      } catch (_) { }
    };

    fetchProactive();
    return () => { ignore = true; };
  }, []);

  function runProactiveCheck() {
    api('/api/aidey/proactive')
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => {
        if (!data?.nudge?.message) return;
        setChatMessages((prev) => {
          const hasExisting = prev.some((m) => m.role === 'assistant' && m.meta?.nudgeType === data.nudge.type);
          if (hasExisting) return prev;
          return [...prev, { role: 'assistant', content: data.nudge.message, ts: Date.now(), meta: { nudgeType: data.nudge.type } }];
        });
      })
      .catch(() => { });
  }

  // Check for a recent session on popup open (only when not resuming an existing session)
  useEffect(() => {
    if (!isPopup || sessionIdProp) return;
    let ignore = false;
    getLastDbSession().then(session => {
      if (ignore || !session) return;
      const relTime = (() => {
        const diffMins = Math.floor((Date.now() - new Date(session.updatedAt).getTime()) / 60000);
        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins}m ago`;
        const h = Math.floor(diffMins / 60);
        return h < 24 ? `${h}h ago` : null;
      })();
      if (!relTime) return; // > 24h, skip
      setChatMessages(prev => {
        // Don't add if already showing resume card
        if (prev.some(m => m.role === 'session_resume')) return prev;
        // Replace the initial greeting with the resume card
        const withoutGreeting = prev.filter(m => !(m.role === 'assistant' && m.type === 'greeting'));
        return [...withoutGreeting, { role: 'session_resume', sessionId: session.id, sessionTitle: session.title || 'your last conversation', sessionTime: relTime, ts: Date.now() }];
      });
    });
    return () => { ignore = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load unread delegation handover messages on mount and prepend them to chat
  useEffect(() => {
    let ignore = false;
    api('/api/absence-delegation/messages/unread')
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (ignore || !data?.messages?.length) return;
        const ids = data.messages.map(m => m.id);
        const msgs = data.messages.map(m => ({
          role: 'assistant',
          content: m.message,
          ts: new Date(m.createdAt).getTime(),
          meta: { delegationMessageId: m.id },
        }));
        setChatMessages(prev => {
          const alreadySeen = ids.every(id => prev.some(m => m.meta?.delegationMessageId === id));
          if (alreadySeen) return prev;
          return [...msgs, ...prev];
        });
        api('/api/absence-delegation/messages/mark-read', {
          method: 'POST',
          body: JSON.stringify({ ids }),
        }).catch(() => {});
      })
      .catch(() => {});
    return () => { ignore = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  useEffect(() => {
    const chatDiv = chatScrollRef.current;
    if (!chatDiv) return;
    // Auto-scroll down on new message (debounced)
    setTimeout(() => {
      chatDiv.scrollTop = chatDiv.scrollHeight;
    }, 10);
    return () => { };
  }, [chatMessages]);

  // Persist messages into the session store
  useEffect(() => {
    persistMessages(sessionIdRef.current, chatMessages);
    if (onSessionUpdated) onSessionUpdated(sessionIdRef.current);
  }, [chatMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  function addMessage(role, content) {
    setChatMessages(prev => [...prev, { role, content, ts: Date.now() }]);
  }

  function addLoadingMessage() {
    setChatMessages(prev => [...prev, { role: 'assistant', content: '…', isTyping: true, ts: Date.now() }]);
  }

  // ── Approval card handlers ─────────────────────────────────────────────────
  async function handleApprovalConfirm(msg, editedBody) {
    const { approvalData } = msg;
    const tool = approvalData.tool;
    const input = { ...(approvalData.input || {}) };

    if (editedBody !== undefined) {
      input.draft_body = editedBody;
      input.body = editedBody;
    }

    // Legacy email send (collected via step-by-step flow, not Anthropic tool-use)
    if (tool === 'send_email_legacy') {
      const emailInput = approvalData.input || {};
      const toAddr = emailInput.to;
      const body = editedBody !== undefined ? editedBody : emailInput.body;
      setChatMessages(prev => prev.map(m =>
        m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
      ));
      try {
        const res = await api('/api/email/send', {
          method: 'POST',
          body: JSON.stringify({ to: toAddr, subject: emailInput.subject, body }),
        });
        if (!res.ok) {
          const payload = await res.json().catch(() => ({}));
          throw new Error(payload?.error || 'Send failed');
        }
        setChatMessages(prev => prev.map(m =>
          m.isTyping ? { role: 'assistant', content: `Sent. Your email to **${toAddr}** is on its way.`, ts: Date.now() } : m
        ));
      } catch (err) {
        setChatMessages(prev => prev.map(m =>
          m.isTyping ? { role: 'assistant', content: `That email didn't send. ${err.message}. Want me to try again?`, ts: Date.now() } : m
        ));
      }
      setPendingTask(null);
      return;
    }

    // Inject writing style for invoice chases
    if (tool === 'send_invoice_chase') {
      const styleProfile = loadStyleProfile(currentUser?.id);
      const learned = loadLearnedPatterns(currentUser?.id);
      input.writing_style = {
        toneKeywords: styleProfile?.toneKeywords || [],
        wordsToAvoid: styleProfile?.wordsToAvoid || [],
        signOff: styleProfile?.signOff || '',
        learnedSampleCount: learned?.sampleCount || 0,
      };
    }

    // Inject synthetic user message so next AI turn knows what was confirmed
    setChatMessages(prev => [
      ...prev,
      { role: 'user', content: buildConfirmSummary(tool, input), synthetic: true, ts: Date.now() },
    ]);

    // Replace card with loading placeholder
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
    ));

    try {
      const result = await confirmAideyTool(tool, input);
      setPendingToolConfirmation(null);
      fireToolEvents([tool]);
      const summary = formatConfirmedActionSummary(tool, result);
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: summary, ts: Date.now() } : m
      ));
    } catch (err) {
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `That didn't go through. ${err.message}. Let me know if you'd like to retry.`, ts: Date.now() } : m
      ));
    }
  }

  function handleApprovalCancel(msg) {
    setPendingToolConfirmation(null);
    setPendingTask(null);
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: 'No action taken.', ts: Date.now() } : m
    ));
  }

  async function handleWorkloadConfirm(msg, approvedSuggestions) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
    ));
    try {
      const result = await confirmAideyTool('redistribute_workload', { approvedSuggestions });
      const summary = formatConfirmedActionSummary('redistribute_workload', result);
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: summary, ts: Date.now() } : m
      ));
    } catch (err) {
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `Couldn't move those tasks. ${err.message}. Let me know if you'd like to try again.`, ts: Date.now() } : m
      ));
    }
  }

  function handleWorkloadCancel(msg) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: 'No tasks moved.', ts: Date.now() } : m
    ));
  }

  async function handleAbsenceDelegationConfirm(msg, { leaveRequestId, delegations }) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: '…', isTyping: true, ts: Date.now() } : m
    ));
    try {
      const res = await api('/api/absence-delegation/execute', {
        method: 'POST',
        body: JSON.stringify({ leaveRequestId, delegations }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || 'Delegation failed');
      const names = (data.summary || []).join('\n');
      window.dispatchEvent(new Event('aidey:tasksUpdated'));
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `Done — I've delegated ${data.delegated} task${data.delegated === 1 ? '' : 's'} and sent a handover note to each person.${names ? `\n\n${names}` : ''}`, ts: Date.now() } : m
      ));
    } catch (err) {
      setChatMessages(prev => prev.map(m =>
        m.isTyping ? { role: 'assistant', content: `Couldn't complete the delegation: ${err.message}`, ts: Date.now() } : m
      ));
    }
  }

  function handleAbsenceDelegationCancel(msg) {
    setChatMessages(prev => prev.map(m =>
      m.cardId === msg.cardId ? { role: 'assistant', content: 'No tasks delegated.', ts: Date.now() } : m
    ));
  }

  // ── Session save on close ────────────────────────────────────────────────

  async function handleClose() {
    const userMessages = chatMessages.filter(m => m.role === 'user' && m.content);
    if (userMessages.length > 0) {
      const sessionId = sessionIdRef.current;
      const existing = loadSession(sessionId);
      let title = existing?.title;
      if (!title || title === userMessages[0].content.slice(0, 60)) {
        title = await generateSessionTitle(userMessages[0].content);
        // Update localStorage title too
        if (existing) {
          persistMessages(sessionId, chatMessages);
        }
      }
      // Save to DB (fire-and-forget)
      saveSessionToDb({
        id: sessionId,
        title,
        messages: chatMessages,
        createdAt: existing?.createdAt || new Date().toISOString(),
        projectId: projectIdProp,
      });
    }
    onClose?.();
  }

  // Expose handleClose to parent via ref so the popup titlebar can trigger a proper save
  if (closeRef) closeRef.current = handleClose;

  // ── Session resume on reopen ─────────────────────────────────────────────

  async function handleSessionContinue(sessionId) {
    setChatMessages(prev => prev.filter(m => m.role !== 'session_resume'));
    // Load full messages from DB
    const session = await loadSessionFromDb(sessionId);
    if (session?.messages?.length) {
      sessionIdRef.current = session.id;
      setChatMessages(session.messages);
    }
  }

  function handleSessionFresh() {
    setChatMessages(prev => prev.filter(m => m.role !== 'session_resume'));
    setChatMessages([{ role: 'assistant', content: aideyGreeting(isChildWorkspace), ts: Date.now(), type: 'greeting' }]);
  }

  // Helper to push an approval card message into the chat
  function addApprovalCard(approvalData) {
    const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
    setChatMessages(prev => [...prev, { role: 'approval', approvalData, cardId, ts: Date.now() }]);
    return cardId;
  }

  // === @MENTION HANDLING ===
  function handleInputChange(e) {
    const value = e.target.value;
    setChatInput(value);
    // Typing clears the voice flag so only full voice submissions trigger intent detection
    wasVoiceInputRef.current = false;

    // Check for @mentions
    const lastAtIndex = value.lastIndexOf('@');
    if (lastAtIndex !== -1) {
      const afterAt = value.slice(lastAtIndex + 1);

      // Only show suggestions if the @ is at the beginning of a word
      const beforeAt = value.slice(0, lastAtIndex);
      const isValidMention = beforeAt === '' || /\s$/.test(beforeAt);

      if (isValidMention && !afterAt.includes(' ')) {
        // Suggest only the active workspace's mentionable (account-holding) people.
        const q = afterAt.toLowerCase();
        const suggestions = mentionableMembers
          .filter(m => (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q))
          .map(m => ({
            id: m.userId,
            name: m.name,
            email: m.email,
            handle: `@${(m.name || '').toLowerCase().replace(/\s+/g, '')}`,
            color: m.color,
            avatar: m.avatar,
          }));
        setMentionSuggestions(suggestions);
        setMentionQuery(afterAt);
        setShowMentions(suggestions.length > 0);
      } else {
        setShowMentions(false);
      }
    } else {
      setShowMentions(false);
    }
  }

  function selectMention(suggestion) {
    const lastAtIndex = chatInput.lastIndexOf('@');
    const beforeAt = chatInput.slice(0, lastAtIndex);
    const afterMention = chatInput.slice(lastAtIndex + 1 + mentionQuery.length);

    setChatInput(beforeAt + suggestion.handle + ' ' + afterMention);
    setShowMentions(false);
    setMentionSuggestions([]);
    setMentionQuery('');
  }

  function handleKeyDown(e) {
    if (e.key === 'Escape' && showMentions) {
      setShowMentions(false);
      e.preventDefault();
    }
  }

  // === CHAT SUBMIT HANDLER (handles multi-action!) ===
  async function handleChatSubmit(e) {
    e.preventDefault();
    const input = chatInput.trim();
    if (!input) return;

    addMessage("user", input);
    setChatInput('');
    runProactiveCheck();

    // Meeting Mode attendee collection
    if (pendingMeetingAttendees !== null) {
      const raw = input.trim();
      const attendees = /^(just me|no one|none|solo|only me)$/i.test(raw)
        ? []
        : raw.split(/[,;&]+/).map(n => ({ name: n.trim() })).filter(a => a.name.length > 0);
      activateMeetingMode(attendees, pendingMeetingAttendees.title || '');
      setChatInput('');
      return;
    }

    if (pendingToolConfirmation) {
      const yes = /^(yes|y|confirm|approve|go ahead|do it|send it|proceed|ok|sure)/i.test(input);
      const no = /^(no|n|cancel|stop|don't|do not|no thanks)/i.test(input);

      if (yes) {
        addLoadingMessage();
        const confirmInput = { ...(pendingToolConfirmation.input || {}) };
        if (pendingToolConfirmation.tool === 'send_invoice_chase') {
          const styleProfile = loadStyleProfile(currentUser?.id);
          const learned = loadLearnedPatterns(currentUser?.id);
          confirmInput.writing_style = {
            toneKeywords: styleProfile?.toneKeywords || [],
            wordsToAvoid: styleProfile?.wordsToAvoid || [],
            signOff: styleProfile?.signOff || '',
            learnedSampleCount: learned?.sampleCount || 0,
          };
        }

        confirmAideyTool(pendingToolConfirmation.tool, confirmInput)
          .then((result) => {
            const confirmedTool = pendingToolConfirmation.tool;
            setPendingToolConfirmation(null);
            const summary = formatConfirmedActionSummary(confirmedTool, result);
            setChatMessages((prev) => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: summary, ts: Date.now() },
            ]);
            return result;
          })
          .catch((err) => {
            setChatMessages((prev) => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: `That didn't complete. ${err.message}. Let me know if you'd like to retry.`, ts: Date.now() },
            ]);
          });
        setChatInput("");
        return;
      }

      if (no) {
        setPendingToolConfirmation(null);
        addMessage('assistant', 'No action taken.');
        setChatInput('');
        return;
      }
    }

    // Allow users to cancel or start over at any time
    if (/^(cancel|stop|abort|start\s*over|never\s*mind|exit)$/i.test(input)) {
      setPendingTask(null);
      setPendingField(null);
      setVoiceConfirmation(null);
      wasVoiceInputRef.current = false;
      addMessage("assistant", "Cancelled. Nothing's been changed.");
      setChatInput("");
      return;
    }

    if (isMeetingTranscribing && /stop\s+transcrib/i.test(input)) {
      stopMeetingTranscription();
      setChatInput('');
      return;
    }

    // Tool-first orchestration: let Claude decide actions and tools before legacy parser fallback.
    const historyForAI = [
      ...chatMessages
        .filter((m) => (m.role === 'user' || m.role === 'assistant') && !m.isTyping)
        .map((m) => ({ role: m.role, content: m.content })),
      { role: 'user', content: input },
    ];

    addLoadingMessage();
    let aiData = null;
    try {
      aiData = await callAideyAI(historyForAI, {});
    } catch (_) {
      aiData = null;
    }

    if (aiData?.__creditsError) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: "My AI engine is temporarily unavailable — the Anthropic account needs credits. Basic commands (create task, schedule meeting) still work, but conversational AI responses won't be available until credits are topped up.", ts: Date.now() },
      ]);
      setChatInput('');
      return;
    }

    if (!aiData) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: "Aidey timed out — please try again.", ts: Date.now() },
      ]);
      setChatInput('');
      return;
    }

    const aiReply = aiData?.reply?.trim();

    // Detect a JSON action directive from the backend prompt (e.g.
    // {"action":"open_document_importer"}). Dispatch map keeps this extensible
    // and — critically — the unknown-action fallback stops any improvised/
    // undocumented action from leaking raw JSON into the chat (Block 109/110).
    if (aiReply) {
      let parsedAction = null;
      try {
        const stripped = aiReply.replace(/^```(?:json)?\s*/i, '').replace(/\s*```\s*$/, '').trim();
        const parsed = JSON.parse(stripped);
        if (parsed && typeof parsed.action === 'string') parsedAction = parsed.action;
      } catch { /* not a JSON action, carry on to normal reply handling */ }

      if (parsedAction) {
        const ACTION_HANDLERS = {
          open_document_importer: () => {
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: "Sure — opening the document importer now. Upload a PDF, DOCX, or TXT file (or paste a Google Doc link) and I'll turn it into a structured project.", ts: Date.now() },
            ]);
            if (typeof onOpenDocumentImporter === 'function') onOpenDocumentImporter();
          },
          open_meeting_transcripts: () => {
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              { role: 'assistant', content: "Opening your meeting transcripts now — pick Zoom, Google Meet, Teams, or upload a transcript, and I'll process it.", ts: Date.now() },
            ]);
            setZoomPanelOpen(true);
          },
        };

        const handler = ACTION_HANDLERS[parsedAction];
        if (handler) {
          handler();
        } else {
          // Unknown/improvised action — never render raw JSON to the user.
          console.warn('[Aidey chat] Unknown action from backend:', parsedAction);
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'assistant', content: "Sorry, I couldn't complete that action.", ts: Date.now() },
          ]);
        }
        setChatInput('');
        return;
      }
    }

    // Integration check must come first — toolUsed is also true when integration is missing
    if (aiData?.integrationRequired) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'integration-required', integrationName: aiData.integrationName, ts: Date.now() },
      ]);
      setChatInput('');
      return;
    }

    if (aiData?.needsConfirmation || aiData?.emailDraft || aiData?.toolUsed) {
      if (aiData?.needsConfirmation) {
        const nc = aiData.needsConfirmation;
        if (nc.type === 'workload_redistribution') {
          const cardId = `workload-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages((prev) => [
            ...prev.slice(0, -1),
            ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
            { role: 'workload_redistribution', suggestions: nc.suggestions || [], cardId, ts: Date.now() + 1 },
          ]);
        } else if (nc.type === 'absence_delegation') {
          const cardId = `absence-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages((prev) => [
            ...prev.slice(0, -1),
            ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
            { role: 'absence_delegation', absenceData: nc.absenceData, leaveRequestId: nc.input?.leave_request_id, cardId, ts: Date.now() + 1 },
          ]);
        } else {
          setPendingToolConfirmation(nc);
          const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages((prev) => [
            ...prev.slice(0, -1),
            ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
            { role: 'approval', approvalData: nc, cardId, ts: Date.now() + 1 },
          ]);
        }
      } else if (aiData?.emailDraft) {
        const cardId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setChatMessages((prev) => [
          ...prev.slice(0, -1),
          ...(aiReply ? [{ role: 'assistant', content: aiReply, ts: Date.now() }] : []),
          { role: 'email_draft', emailDraft: aiData.emailDraft, cardId, ts: Date.now() + 1 },
        ]);
      } else {
        // Tool executed — fire refresh events so UI updates immediately
        fireToolEvents(aiData.toolNamesUsed);
        setChatMessages((prev) => [
          ...prev.slice(0, -1),
          { role: 'assistant', content: aiReply || 'Done.', ts: Date.now() },
        ]);
      }
      setChatInput('');
      return;
    }

    if (aiReply) {
      setChatMessages((prev) => [
        ...prev.slice(0, -1),
        { role: 'assistant', content: aiReply, ts: Date.now() },
      ]);
      setChatInput('');
      return;
    }

    // Remove loading placeholder before legacy fallback.
    setChatMessages((prev) => (prev[prev.length - 1]?.isTyping ? prev.slice(0, -1) : prev));

    // ── Voice input: route through Claude pipeline same as typed text ──────
    if (wasVoiceInputRef.current) {
      wasVoiceInputRef.current = false;
    }

    // MULTI-ACTION: If parseUserInput returns array, loop over actions!
    const detailsList = parseUserInput(input, mentionableMembers);
    if (Array.isArray(detailsList)) {
      detailsList.filter(Boolean).forEach(action => handleSingleAction(action));
    } else {
      handleSingleAction(detailsList);
    }
    setChatInput("");
  }

  // ── Voice confirmation handlers ───────────────────────────────────────────
  function handleVoiceConfirm() {
    if (!voiceConfirmation) return;
    const { intent, summary, data } = voiceConfirmation;
    setVoiceConfirmation(null);

    if (intent === 'create_task' || intent === 'create_meeting') {
      const taskData = {
        title: data.title || summary,
        taskType: intent === 'create_meeting' ? 'Meeting' : 'Task',
        datetime: data.datetime || data.dueDate || null,
        priority: data.priority || 'normal',
        completed: false,
      };
      if (onCreateTask) onCreateTask(taskData, () => { });
      addMessage("assistant", `Done, "${data.title || summary}" is in your ${intent === 'create_meeting' ? 'calendar' : 'tasks'}.`);
    } else if (intent === 'create_note') {
      const note = {
        id: String(Date.now()),
        title: data.title || summary,
        text: data.body || '',
        blocks: [],
        created: new Date().toISOString(),
      };
      api('/api/notes', { method: 'POST', body: JSON.stringify(note) })
        .catch(err => console.error('Failed to save note to API:', err));
      window.dispatchEvent(new Event("aidey:notesUpdated"));
      addMessage("assistant", `Done, "${data.title || summary}" saved to your notes.`);
    } else if (intent === 'create_contact') {
      api('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name || summary,
          email: data.email || '',
          phone: data.phone || '',
          source: 'Voice',
          tags: [],
          isNew: true,
          interactions: [],
        }),
      }).then(() => window.dispatchEvent(new Event('aidey:contactsUpdated')))
        .catch(err => console.error('Failed to save contact to API:', err));
      addMessage("assistant", `Done, ${data.name || 'that contact'} is in your contacts.`);
    } else if (intent === 'create_invoice') {
      api('/api/invoices', {
        method: 'POST',
        body: JSON.stringify({
          contactName: data.client || '',
          issueDate: new Date().toISOString().slice(0, 10),
          dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
          status: 'draft',
          lineItems: data.amount ? [{ id: 1, description: data.description || 'Service', qty: 1, unitPrice: parseFloat(data.amount) || 0, tax: 0 }] : [],
        }),
      }).then(() => refreshInvoices()).catch(err => console.error('Failed to create invoice:', err));
      addMessage("assistant", `Done, draft invoice${data.client ? ` for ${data.client}` : ''} saved to Invoicing.`);
    } else {
      addMessage("assistant", "Done.");
    }
  }

  function handleVoiceCancel() {
    setVoiceConfirmation(null);
  }

  // === Single Action Handler ===
  function handleSingleAction(details) {
    if (/transcrib(e|ing).*(meeting|call)|meeting.*transcrib(e|ing)|start.*meeting.*note|meeting.*note|i.?m in a meeting|start meeting mode/i.test(details.rawInput || details.title || '')) {
      addMessage('assistant', "Who's in the meeting with you? List their names separated by commas, or say \"just me\" to start without attendees.");
      setPendingMeetingAttendees({ title: details.title || '' });
      return;
    }

    // TODO: If any field missing, step-by-step ask user
    // This can be expanded for more robust field collection

    // 0z. Writing help — style-injected AI assistance
    if (details.action === "writing-help") {
      addMessage("assistant", "I can help with that. Paste what you've written so far, or describe what you need — I'll suggest improvements that match your voice.");
      setPendingTask(details);
      setPendingField("writingInput");
      return;
    }

    // 0a. Content generation
    if (details.action === "generate-content") {
      const type = details.contentType || "content";
      addMessage("assistant", `Sure! I can draft ${type} for you. What's the topic or brief?`);
      setPendingTask(details);
      setPendingField("contentBrief");
      return;
    }

    // 0a2. Agenda query
    if (details.action === "agenda" || /\b(agenda|what.*today|today.*tasks?|on my schedule|my day)\b/i.test(details.title || "")) {
      const rawInput = details.rawInput || "";
      const isWeek = /\b(this week|next 7 days|week ahead|weekly)\b/i.test(rawInput);
      const isTomorrow = /\btomorrow\b/i.test(rawInput);
      const checkDate = isTomorrow ? getTomorrowISO() : getTodayISO();

      if (isWeek) {
        // Show 7-day summary
        const today = new Date(getTodayISO());
        const lines = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(today);
          d.setDate(d.getDate() + i);
          const ds = toLocalDateStr(d);
          const dayTasks = userSchedule.filter(t => t.date === ds && !t.completed);
          if (dayTasks.length > 0) {
            const dayLabel = i === 0 ? 'Today' : i === 1 ? 'Tomorrow' : d.toLocaleDateString('en-GB', { weekday: 'long' });
            lines.push(`**${dayLabel} (${fmtDate(ds)})**`);
            dayTasks.forEach(t => lines.push(`  • ${t.title}${t.time ? ' at ' + fmtTime(t.time) : ''}`));
          }
        }
        addMessage("assistant", lines.length > 0
          ? `Here's your week ahead:\n\n${lines.join('\n')}`
          : "You have nothing scheduled this week. Want me to add something?");
      } else {
        const label = isTomorrow ? 'tomorrow' : 'today';
        const dayTasks = userSchedule.filter(t => t.date === checkDate && !t.completed);
        if (dayTasks.length === 0) {
          addMessage("assistant", `You have nothing scheduled for ${label}. Want me to add something?`);
        } else {
          const sorted = [...dayTasks].sort((a, b) => (a.time || '').localeCompare(b.time || ''));
          const lines = sorted.map(t => `• ${t.title}${t.time ? ' at ' + fmtTime(t.time) : ''}${t.priority && t.priority !== 'Medium' ? ` (${t.priority})` : ''}`).join("\n");
          addMessage("assistant", `Here's your agenda for ${label}:\n\n${lines}`);
        }
      }
      return;
    }

    // 0b. Find free time slots
    if (details.action === "find-slots") {
      const checkDate = details.date || getTodayISO();
      const busyTasks = userSchedule.filter(t => t.date === checkDate && !t.completed && t.time);
      if (busyTasks.length === 0) {
        addMessage("assistant", `You have no scheduled tasks on ${fmtDate(checkDate)}. You're free all day! Want me to book something?`);
      } else {
        const busyTimes = busyTasks.map(t => t.time).sort().map(fmtTime).join(", ");
        const nextFree = findNextAvailable({ date: checkDate, time: "07:00" }, userSchedule);
        const slotLabel = nextFree
          ? (nextFree.dayOffset > 0 ? `${fmtDate(nextFree.date)} at ${fmtTime(nextFree.time)}` : fmtTime(nextFree.time))
          : "no free slots found today";
        addMessage("assistant", `On ${fmtDate(checkDate)} you're busy at: ${busyTimes}.\nNext available slot: **${slotLabel}**. Want me to book something there?`);
      }
      return;
    }

    // 0c2. Invoice creation from chat
    if (details.action === "create-invoice") {
      const rawInput = details.rawInput || details.title || "";
      const clientMatch = rawInput.match(/(?:invoice\s+for|bill)\s+([A-Za-z\s]+?)(?:\s+for|\s+\$|\s+£|\s+€|\s*$)/i);
      const amountMatch = rawInput.match(/[$£€]?\s*(\d+(?:[.,]\d{2})?)\s*(?:[$£€]|dollars?|pounds?)?/i);
      const client = clientMatch ? clientMatch[1].trim() : null;
      const amount = amountMatch ? parseFloat(amountMatch[1].replace(',', '.')) : null;

      if (!client) {
        addMessage("assistant", "Who is this invoice for? (Give me the client or company name)");
        setPendingTask({ ...details, amount });
        setPendingField("invoiceClient");
        return;
      }
      if (!amount) {
        addMessage("assistant", `Got it — invoice for **${client}**. What's the amount?`);
        setPendingTask({ ...details, client });
        setPendingField("invoiceAmount");
        return;
      }

      // Show preview for approval
      const preview = `📄 **Draft Invoice**\n**Client:** ${client}\n**Amount:** £${amount.toFixed(2)}\n**Description:** ${rawInput}\n**Status:** Draft\n\nDoes this look right? Say **"save it"** to add to your invoices, or **"cancel"** to start over.`;
      addMessage("assistant", preview);
      setPendingTask({ ...details, client, amount, rawInput });
      setPendingField("invoiceApproval");
      return;
    }

    // 0c2b. Send booking link via email
    if (details.action === "send-booking-link") {
      const rawInput = details.rawInput || "";
      const toMatch = rawInput.match(/\bto\s+([A-Za-z][A-Za-z\s]+?)(?:\s+my|\s+a|\s+the|\s*$)/i);
      const recipientName = toMatch ? toMatch[1].trim() : null;

      // Try to find their email in contacts
      let recipientEmail = null;
      if (recipientName) {
        const match = contacts.find(c => c.name?.toLowerCase().includes(recipientName.toLowerCase()));
        recipientEmail = match?.email || null;
      }

      // Get the user's scheduling link
      let bookingUrl = null;
      try {
        const types = schedulingManager.getEventTypes();
        if (types.length > 0) bookingUrl = schedulingManager.getBookingUrl(types[0].id, currentUser?.bookingUsername);
      } catch { }

      if (!bookingUrl) {
        addMessage("assistant", "I couldn't find your scheduling link. Set up your Scheduling Hub first, then I can share it.");
        return;
      }

      if (!recipientName) {
        addMessage("assistant", `I'll share your booking link. Who should I send it to?`);
        setPendingTask({ bookingUrl });
        setPendingField("bookingLinkRecipient");
        return;
      }

      const emailLine = recipientEmail
        ? `\n\nReady to send — [click here to open in your email app](mailto:${recipientEmail}?subject=${encodeURIComponent('Book a time with me')}&body=${encodeURIComponent(`Hi ${recipientName},\n\nFeel free to book a time that works for you:\n\n${bookingUrl}\n\nLooking forward to speaking!`)})`
        : `\n\nI don't have an email for ${recipientName}. What's their address?`;

      addMessage("assistant", `Here's your booking link to share with **${recipientName}**:\n\n📅 ${bookingUrl}${emailLine}`);

      if (!recipientEmail) {
        setPendingTask({ bookingUrl, recipientName });
        setPendingField("bookingLinkEmail");
      }
      return;
    }

    // 0c3a. Add attendee to existing meeting
    if (details.action === "add-attendee") {
      const personName = details.addPersonName;
      const meetingHint = details.addToMeetingTitle;

      if (!personName) {
        addMessage("assistant", "Who would you like to add? Just give me their name.");
        return;
      }

      // Look up contact email
      const contact = contacts.find(c => c.name?.toLowerCase().includes(personName.toLowerCase()));
      const email = contact?.email || null;

      // Find matching meeting
      const matchedMeeting = meetingHint
        ? userSchedule.find(t => t.title.toLowerCase().includes(meetingHint.toLowerCase()))
        : null;

      if (meetingHint && !matchedMeeting) {
        // Show upcoming meetings to pick from
        const upcoming = userSchedule
          .filter(t => t.taskType === 'Meeting' || /meeting|call|appointment/i.test(t.title || ''))
          .slice(0, 5);
        if (upcoming.length > 0) {
          const opts = upcoming.map(t => `• "${t.title}"${t.date ? ` on ${fmtDate(t.date)}` : ''}`).join('\n');
          addMessage("assistant", `I couldn't find a meeting called "${meetingHint}". Did you mean one of these?\n\n${opts}`);
        } else {
          addMessage("assistant", `I couldn't find a meeting called "${meetingHint}". Which meeting should I add ${personName} to?`);
        }
        setPendingTask({ ...details, personName, contact, email });
        setPendingField("addAttendeeMeetingTitle");
        return;
      }

      if (matchedMeeting) {
        const when = fmtDateTime(matchedMeeting.date, matchedMeeting.time);
        const emailLine = email
          ? `\n\nShall I draft an email invite for ${personName}?`
          : `\n\nDo you have an email address for ${personName} so I can send them an invite?`;
        addMessage("assistant", `Got it — adding ${personName} to "${matchedMeeting.title}"${when ? ` on ${when}` : ''}.${emailLine}`);
        setPendingTask({ ...details, meeting: matchedMeeting, contact, personName, email });
        setPendingField("attendeeEmailConfirm");
      } else {
        // No meeting hint at all — list upcoming meetings
        const upcoming = userSchedule
          .filter(t => t.taskType === 'Meeting' || /meeting|call|appointment/i.test(t.title || ''))
          .slice(0, 5);
        if (upcoming.length > 0) {
          const opts = upcoming.map(t => `• "${t.title}"${t.date ? ` on ${fmtDate(t.date)}` : ''}`).join('\n');
          addMessage("assistant", `Which meeting would you like to add ${personName} to?\n\n${opts}`);
        } else {
          addMessage("assistant", `Which meeting should I add ${personName} to?`);
        }
        setPendingTask({ ...details, personName, contact, email });
        setPendingField("addAttendeeMeetingTitle");
      }
      return;
    }

    // 0c3. Travel intent
    if (details.action === "travel") {
      addMessage("assistant", "I can help with travel planning. Tell me where you need to be and when, and I'll create a travel task with any relevant booking links. Want to schedule something now?");
      return;
    }

    // 0c. Email commands
    if (details.action === "email-command") {
      const rawInput = details.rawInput || details.title || "";

      // Check if user wants to draft/send a specific email
      const isDraft = /\b(draft|compose|write|send)\b/i.test(rawInput);
      const toMatch = rawInput.match(/\b(?:email|mail|message)\s+(?:to\s+)?([A-Za-z][A-Za-z\s]+?)(?:\s+about|\s+re:|\s+regarding|\s*$)/i);
      const aboutMatch = rawInput.match(/\b(?:about|re:|regarding)\s+(.+)/i);
      const recipientName = toMatch ? toMatch[1].trim() : null;
      const subjectHint = aboutMatch ? aboutMatch[1].trim() : null;

      if (isDraft && recipientName) {
        // Try to find email in contacts
        const recipientMatch = contacts.find(c => c.name?.toLowerCase().includes(recipientName.toLowerCase()));
        const recipientEmail = recipientMatch?.email || null;

        if (subjectHint) {
          const subjectLine = subjectHint.charAt(0).toUpperCase() + subjectHint.slice(1);
          addMessage("assistant", `Drafting an email to **${recipientName}** about "${subjectLine}". What should the email say? (Or say "generate" and I'll write a draft for you)`);
          setPendingTask({ ...details, emailTo: recipientEmail || recipientName, emailToName: recipientName, emailSubject: subjectLine });
          setPendingField("emailBody");
        } else {
          addMessage("assistant", `Sure, I'll draft an email to **${recipientName}**. What's it about?`);
          setPendingTask({ ...details, emailTo: recipientEmail || recipientName, emailToName: recipientName });
          setPendingField("emailSubject");
        }
        return;
      }

      if (isDraft) {
        addMessage("assistant", "Who is this email for?");
        setPendingTask(details);
        setPendingField("emailTo");
        return;
      }

      // General email help
      addMessage("assistant", "I can draft an email for you, send your booking link, or open your inbox. Just tell me what you need.");
      return;
    }

    // 0d. Contact management
    if (details.action === "contacts") {
      const rawInput = details.rawInput || details.title || "";
      const lower = rawInput.toLowerCase();

      // SHOW / LIST
      if (/show contacts|list contacts|all contacts/.test(lower)) {
        if (contacts.length === 0) {
          addMessage("assistant", "You have no contacts saved yet. Say \"add contact\" to create one.");
        } else {
          const lines = contacts.slice(0, 10).map(c => `• **${c.name}**${c.company ? ` (${c.company})` : ""}${c.email ? ` — ${c.email}` : ""}${c.tags?.length ? ` [${c.tags.join(", ")}]` : ""}`).join("\n");
          addMessage("assistant", `You have ${contacts.length} contact${contacts.length !== 1 ? "s" : ""}:\n${lines}${contacts.length > 10 ? `\n…and ${contacts.length - 10} more. Open CRM for the full list.` : ""}`);
        }
        return;
      }

      // FIND / SEARCH
      const findMatch = rawInput.match(/find contact[s]?\s+(.+)/i) || rawInput.match(/search contact[s]?\s+(.+)/i) || rawInput.match(/show contact[s]?\s+(.+)/i);
      if (findMatch) {
        const q = findMatch[1].trim().toLowerCase();
        const found = contacts.filter(c =>
          c.name?.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q) ||
          c.company?.toLowerCase().includes(q)
        );
        if (found.length === 0) {
          addMessage("assistant", `No contacts found matching "${findMatch[1].trim()}". Try a different name, email or company.`);
        } else {
          const lines = found.slice(0, 5).map(c => `• **${c.name}**${c.company ? ` · ${c.company}` : ""}${c.email ? ` · ${c.email}` : ""}${c.phone ? ` · ${c.phone}` : ""}${c.tags?.length ? ` [${c.tags.join(", ")}]` : ""}`).join("\n");
          addMessage("assistant", `Found ${found.length} match${found.length !== 1 ? "es" : ""}:\n${lines}`);
        }
        return;
      }

      // ADD / NEW
      if (/add contact|new contact|create contact/.test(lower)) {
        const nameMatch = rawInput.match(/(?:add|new|create)\s+contact\s+(.+)/i);
        const extractedName = nameMatch ? nameMatch[1].trim() : null;
        if (extractedName) {
          api('/api/contacts', {
            method: 'POST',
            body: JSON.stringify({
              name: extractedName,
              email: '', phone: '', company: '',
              tags: [], source: 'Aidey Chat', isNew: true,
              notes: '', interactions: [],
            }),
          }).then(() => window.dispatchEvent(new Event('aidey:contactsUpdated')))
            .catch(err => console.error('Failed to save contact:', err));
          addMessage("assistant", `✅ Contact **${extractedName}** added! You can add their email, phone and tags in the CRM.\n\nWould you like to open Contacts now?`);
        } else {
          addMessage("assistant", "What's the name of the contact you'd like to add?");
          setPendingTask({ ...details, contactAction: "add" });
          setPendingField("contactName");
        }
        return;
      }

      // Fallback
      addMessage("assistant", "I can show your contacts, search for someone, or add a new one. What would you like to do?");
      return;
    }

    // 0e. Meeting notes → navigate to MeetingRecorder or inline paste
    if (details.action === "zoom-transcripts") {
      setZoomPanelOpen(true);
      addMessage('assistant', 'Opening your recent meeting transcripts — Zoom, Google Meet, and Teams tabs are available. Any recordings with transcripts from the last 7 days will appear.');
      return;
    }

    if (details.action === "meeting-notes") {
      addMessage("assistant",
        `Let's turn a meeting into tasks. Do you already have a transcript, or want to record / paste one now?`
      );
      setTimeout(() => setMeetingChoiceOpen(true), 600);
      return;
    }

    // 1. Move
    if (details.action === "move") {
      const toMove = userSchedule.find(
        t => details.title && t.title.toLowerCase().includes(details.title.toLowerCase())
      );
      if (!toMove) {
        addMessage("assistant", "Which meeting or task do you want to move?");
        setPendingTask(details);
        setPendingField("title");
        return;
      }
      if (!details.date && !details.time) {
        addMessage("assistant", `What date/time should I move "${toMove.title}" to?`);
        setPendingTask({ ...details, moveTaskId: toMove.id });
        setPendingField("date");
        return;
      }
      onMoveTask(toMove.id, details.date, details.time);
      addMessage("assistant", `Moved "${toMove.title}" to ${fmtDateTime(details.date || toMove.date, details.time || toMove.time)}.`);
      return;
    }

    // 2. Cancel
    if (details.action === "cancel") {
      const toCancel = userSchedule.find(
        t => details.title && t.title.toLowerCase().includes(details.title.toLowerCase())
      );
      if (!toCancel) {
        addMessage("assistant", "Which meeting or task do you want to cancel?");
        setPendingTask(details);
        setPendingField("title");
        return;
      }
      onCancelTask(toCancel.id);
      addMessage("assistant", `Cancelled "${toCancel.title}".`);
      return;
    }

    // 3. Create (default)
    // If no structured task/meeting info was detected, treat as general chat → AI
    const looksLikeTask = details.title || details.taskType || details.date || details.time;
    if (!looksLikeTask) {
      addLoadingMessage();
      // Build conversation history for context (full history, no slice)
      const historyForAI = chatMessages
        .filter(m => (m.role === 'user' || m.role === 'assistant') && !m.isTyping)
        .map(m => ({ role: m.role, content: m.content }));
      // Replace trailing "…" placeholder with real reply
      callAideyAI(historyForAI, {}).then((data) => {
        const reply = data?.reply?.trim() || null;
        if (data?.needsConfirmation) {
          const nc = data.needsConfirmation;
          if (nc.type === 'workload_redistribution') {
            const cardId = `workload-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
              { role: 'workload_redistribution', suggestions: nc.suggestions || [], cardId, ts: Date.now() + 1 },
            ]);
          } else if (nc.type === 'absence_delegation') {
            const cardId = `absence-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
              { role: 'absence_delegation', absenceData: nc.absenceData, leaveRequestId: nc.input?.leave_request_id, cardId, ts: Date.now() + 1 },
            ]);
          } else {
            setPendingToolConfirmation(nc);
            const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
            setChatMessages(prev => [
              ...prev.slice(0, -1),
              ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
              { role: 'approval', approvalData: nc, cardId, ts: Date.now() + 1 },
            ]);
          }
        } else if (data?.emailDraft) {
          const cardId = `draft-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            ...(reply ? [{ role: 'assistant', content: reply, ts: Date.now() }] : []),
            { role: 'email_draft', emailDraft: data.emailDraft, cardId, ts: Date.now() + 1 },
          ]);
        } else {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'assistant', content: reply || "I didn't quite catch that. I can book meetings, sort tasks, draft emails and invoices, or dig through your contacts. What do you need?", ts: Date.now() },
          ]);
        }
      });
      return;
    }

    // Step-by-step field collection for essential fields first
    const essentialFields = [
      { key: "title", prompt: "What should I call this meeting or task?" },
      { key: "date", prompt: "Which date should I schedule this for?" },
      { key: "time", prompt: "What time works best for you?" },
    ];

    // Check for missing essential fields first
    for (const field of essentialFields) {
      if (!details[field.key]) {
        setPendingTask(details);
        setPendingField(field.key);
        addMessage("assistant", field.prompt);
        return;
      }
    }

    // CONFLICT CHECK
    const conflict = findConflict(details, userSchedule, googleEvents, icalEvents, outlookEvents);
    if (conflict) {
      const nextSlotResult = findNextAvailable(details, userSchedule, details.durationMinutes);
      const prioritySwap = suggestPrioritySwap(conflict, details.priority || "Medium");

      const slotLabel = nextSlotResult
        ? (nextSlotResult.dayOffset > 0
          ? `${fmtDate(nextSlotResult.date)} at ${fmtTime(nextSlotResult.time)}`
          : fmtTime(nextSlotResult.time))
        : null;

      let conflictMsg = `You've already got "${conflict.title}" at ${fmtTime(details.time)} on ${fmtDate(details.date)}.`;

      if (slotLabel) {
        conflictMsg += ` The next free slot is ${slotLabel} — want me to book it there instead?`;
        if (prioritySwap) {
          conflictMsg += ` I can also move "${conflict.title}" out of the way if you need ${fmtTime(details.time)} specifically.`;
        }
        conflictMsg += ` Or say "book both" to keep them at the same time.`;
      } else {
        conflictMsg += ` Want to pick a different time, or book it anyway?`;
      }

      addMessage("assistant", conflictMsg);
      setPendingTask({
        ...details,
        suggestion: nextSlotResult?.time,
        suggestionDate: nextSlotResult?.date,
        conflictingEvent: conflict,
      });
      setPendingField("resolveConflict");
      return;
    }

    // Check if this is a meeting and needs meeting mode specification
    const isMeeting = details.title && (
      details.title.toLowerCase().includes('meeting') ||
      details.title.toLowerCase().includes('call') ||
      details.title.toLowerCase().includes('appointment')
    );

    if (isMeeting && !details.hasOwnProperty('meetingMode')) {
      setPendingTask(details);
      setPendingField("meetingMode");
      addMessage("assistant", "Is this meeting in person or online?");
      return;
    }

    // Handle online meeting platform selection
    if (details.meetingMode === "online" && !details.hasOwnProperty('meetingType')) {
      // ✅ Check user's default meeting type preference first
      const userProfile = JSON.parse(localStorage.getItem("userProfile") || "{}");
      const defaultMeetingType = userProfile?.preferences?.defaultMeetingType;

      if (defaultMeetingType && defaultMeetingType.trim() !== "") {
        // Use the user's default meeting type
        details.meetingType = defaultMeetingType;
        addMessage("assistant", `Using your default meeting platform: ${defaultMeetingType}`);
        // Continue processing with the default meeting type
      } else {
        // No default set, ask the user
        setPendingTask(details);
        setPendingField("meetingType");
        addMessage("assistant", "Which platform do you want to use for the online meeting? (Zoom, Google Meet, Teams, Other)");
        return;
      }
    }

    // Handle location based on meeting mode
    if (details.meetingMode === "online" && !details.hasOwnProperty('location')) {
      setPendingTask(details);
      setPendingField("location");

      // Provide specific instructions based on meeting type
      if (details.meetingType === "Google Meet") {
        if (isGoogleConnected()) {
          addMessage("assistant", `Would you like to generate a ${details.meetingType} link or paste your own? (Say 'generate' or paste the link)`);
        } else {
          addMessage("assistant", `To auto-generate Google Meet links, you need to connect your Google account first. You can paste a meeting link manually, or type 'generate' after connecting Google Calendar.`);
        }
      } else {
        addMessage("assistant", `Would you like to generate a ${details.meetingType || "meeting"} link or paste your own? (Say 'generate' or paste the link)`);
      }
      return;
    }

    if (details.meetingMode === "in person" && !details.hasOwnProperty('location')) {
      setPendingTask(details);
      setPendingField("location");
      addMessage("assistant", "What is the address or room for the meeting?");
      return;
    }

    // Apply defaults silently — only ask if the user explicitly set these in the original message
    if (!details.hasOwnProperty('priority')) details.priority = "Medium";
    if (!details.hasOwnProperty('reminderOffset')) details.reminderOffset = 15;
    if (!details.hasOwnProperty('details')) details.details = "";

    // Delegate / Assign - Handle task delegation and mentions
    if (details.assignedTo) {
      if (Array.isArray(details.assignedTo)) {
        // Handle @mentions (array of user IDs)
        details.assignedTeam = details.assignedTo;
        details.assignedUsers = details.assignedTo.map(userId => rosterMap.get(userId)).filter(Boolean);
      } else {
        // Handle traditional text assignment
        details.assignedTeam = [details.assignedTo];
      }
      delete details.assignedTo;
    }

    // Handle attendees for meetings
    if (details.attendees && Array.isArray(details.attendees)) {
      details.meetingAttendees = details.attendees.map(userId => rosterMap.get(userId)).filter(Boolean);
    }

    // Add creator information
    details.createdBy = currentUser.id;
    details.createdByUser = currentUser;

    // Apply consistent field fallbacks before task creation
    if (!details.priority) details.priority = "Medium";
    if (!details.reminderOffset && details.reminderOffset !== null) details.reminderOffset = 15;
    if (!details.details) details.details = "";
    if (details.location === undefined) details.location = "";

    // Normalise both field formats so every view can find this task:
    // • datetime + deadline  (used by tasksForDate, sortTasksByUrgency, WeekView)
    // • date + time          (used by CalendarView, getAllTasksForDate, SuperAideyChat)
    if (details.date && details.time && !details.datetime) {
      details.datetime = new Date(`${details.date}T${details.time}`).toISOString();
    }
    if (details.datetime) {
      const dt = new Date(details.datetime);
      details.date = toLocalDateStr(dt);
      details.time = dt.toTimeString().slice(0, 5);
      if (!details.deadline) {
        const durationMs = (details.durationMinutes || 60) * 60 * 1000;
        details.deadline = new Date(dt.getTime() + durationMs).toISOString();
      }
    }

    // All required fields present — create task!
    onCreateTask(details, (message, error) => {
      if (error) {
        addMessage("assistant", `❌ Failed to create task: ${error}`);
        return;
      } else if (message) {
        addMessage("assistant", message);
      }
    });

    // Enhanced confirmation message
    let confirmationMessage = `✅ Scheduled: "${details.title}" for ${fmtDateTime(details.date, details.time)}`;
    if (details.meetingMode === "online" && details.meetingType) {
      confirmationMessage += ` (${details.meetingType} meeting)`;
    } else if (details.meetingMode === "in person" && details.location) {
      confirmationMessage += ` at ${details.location}`;
    }

    // Add assignment/attendee information
    if (details.assignedUsers && details.assignedUsers.length > 0) {
      const assignedNames = details.assignedUsers.map(user => user.name).join(", ");
      confirmationMessage += `\n👥 Assigned to: ${assignedNames}`;
    }

    if (details.meetingAttendees && details.meetingAttendees.length > 0) {
      const attendeeNames = details.meetingAttendees.map(user => user.name).join(", ");
      confirmationMessage += `\n📧 Attendees: ${attendeeNames}`;
    }

    addMessage("assistant", confirmationMessage);

    // Auto-create travel task for in-person meetings (only if user said they need to travel)
    if (details.meetingMode === "in person" && details.location && !details.noTravel && onCreateTask) {
      try {
        const travelPrefs = JSON.parse(localStorage.getItem('aidey_travel_prefs') || '{}');
        const info = details.travelInfo;
        const mode = details.travelMode || info?.recommendedMode || 'driving';
        const modeEmoji = MODE_EMOJI[mode] || '🚗';
        // Use real travel time if available, otherwise pref/default
        const bufferMins = info?.bufferMins || travelPrefs.travelBuffer || 60;
        const meetingDt = new Date(details.datetime || `${details.date}T${details.time || '09:00'}`);
        const travelStart = new Date(meetingDt.getTime() - bufferMins * 60 * 1000);

        const travelPrefsFlightLabel = { 'economy': 'Economy', 'premium-economy': 'Premium economy', 'business': 'Business class', 'first': 'First class' }[travelPrefs.flightClass] || '';
        const travelPrefsHotelLabel = { 'budget': 'Budget', 'mid-range': 'Mid-range', 'premium': 'Premium', 'luxury': 'Luxury / 5-star' }[travelPrefs.hotelTier] || '';

        const links = details.travelFrom
          ? getBookingLinks(details.travelFrom, details.location, mode)
          : null;

        let travelNotes = `${modeEmoji} Travel to: ${details.location}`;
        if (details.travelFrom) travelNotes += `\n📍 From: ${details.travelFrom}`;
        if (info) travelNotes += `\n🗺 Distance: ${info.distanceText} — ${info.travelMins} mins`;
        travelNotes += `\n📅 For: "${details.title}"\n⏰ Arrive by: ${meetingDt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
        if (travelPrefsFlightLabel && mode === 'flight') travelNotes += `\n✈️ Preferred class: ${travelPrefsFlightLabel}`;
        if (travelPrefs.seatPreference && travelPrefs.seatPreference !== 'no-preference' && mode === 'flight') travelNotes += `\n💺 Seat: ${travelPrefs.seatPreference}`;
        if (travelPrefsHotelLabel && (info?.needsHotel || mode === 'flight')) travelNotes += `\n🏨 Hotel preference: ${travelPrefsHotelLabel}`;
        if (travelPrefs.hotelChain && (info?.needsHotel || mode === 'flight')) travelNotes += ` (${travelPrefs.hotelChain})`;
        if (links?.primary) travelNotes += `\n🔗 ${links.primary.label}: ${links.primary.url}`;
        if (links?.secondary) travelNotes += `\n🔗 ${links.secondary.label}: ${links.secondary.url}`;
        if (links?.hotels && (info?.needsHotel || mode === 'flight')) travelNotes += `\n🔗 ${links.hotels.label}: ${links.hotels.url}`;
        if (travelPrefs.notes) travelNotes += `\n📝 ${travelPrefs.notes}`;

        const travelTask = {
          title: `${modeEmoji} Travel to ${details.location}`,
          date: toLocalDateStr(travelStart),
          time: travelStart.toTimeString().slice(0, 5),
          datetime: travelStart.toISOString(),
          deadline: meetingDt.toISOString(),
          durationMinutes: bufferMins,
          priority: 'High',
          category: 'Travel',
          notes: travelNotes,
        };

        onCreateTask(travelTask, () => { });
      } catch { /* silently skip */ }
    }

    // Follow-up prompt — set pendingField so "no thanks" is handled and doesn't re-trigger task creation
    if (isMeeting) {
      setTimeout(() => {
        setPendingField("followUp");
        setPendingTask(details);
        addMessage("assistant", "Anything else for this one — follow-up task, calendar invite, or are we all good?");
      }, details.noTravel ? 400 : 900);
    }

    // Note: Additional confirmation message is handled by the onCreateTask callback
  }

  // === FIELD STEP HANDLER ===
  async function handleFieldInput(input) {
    // Always add the user's input as a visible message first
    addMessage("user", input);
    runProactiveCheck();

    // Allow users to cancel or start over at any time
    if (/^(cancel|stop|abort|start\s*over|never\s*mind|exit)$/i.test(input.trim())) {
      setPendingTask(null);
      setPendingField(null);
      addMessage("assistant", "Cancelled. Nothing's been changed.");
      return;
    }

    const task = { ...pendingTask };
    let proceed = false;

    if (pendingField === "title") {
      task.title = input.trim();
      proceed = true;
    } else if (pendingField === "date") {
      // Accept various date formats
      if (input.toLowerCase().includes("today")) {
        task.date = getTodayISO();
      } else if (input.toLowerCase().includes("tomorrow")) {
        task.date = getTomorrowISO();
      } else {
        task.date = input.trim();
      }
      proceed = true;
    } else if (pendingField === "time") {
      let t = input.trim();
      // Handle various time formats
      const timeMatch = t.match(/(\d{1,2})(?:[.:](\d{2}))?\s*(am|pm)?/i);
      if (timeMatch) {
        let hours = parseInt(timeMatch[1], 10);
        let minutes = timeMatch[2] ? parseInt(timeMatch[2], 10) : 0;
        if (/pm/i.test(timeMatch[3]) && hours < 12) hours += 12;
        if (/am/i.test(timeMatch[3]) && hours === 12) hours = 0;

        // Apply same logic: Assume PM for ambiguous late-hour times (6-11) unless specified
        if (!timeMatch[3] && hours >= 6 && hours <= 11) {
          hours += 12; // Assume PM
        }

        t = `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
      } else if (/^\d{1,2}$/.test(t)) {
        let hours = parseInt(t, 10);
        // Apply same logic for single digit hours
        if (hours >= 6 && hours <= 11) {
          hours += 12; // Assume PM
        }
        t = hours.toString().padStart(2, "0") + ":00";
      }
      task.time = t;
      proceed = true;
    } else if (pendingField === "priority") {
      if (/high/i.test(input)) task.priority = "High";
      else if (/low/i.test(input)) task.priority = "Low";
      else if (/medium/i.test(input)) task.priority = "Medium";
      else if (/^no|none|skip$/i.test(input.trim())) task.priority = "Medium"; // Default fallback
      else {
        addMessage("assistant", "Please say 'high', 'medium', or 'low' priority (or 'skip' for medium).");
        return;
      }
      proceed = true;
    } else if (pendingField === "reminderOffset") {
      if (/^no|none|skip$/i.test(input.trim())) {
        task.reminderOffset = null; // No reminder
      } else if (!isNaN(parseInt(input))) {
        task.reminderOffset = parseInt(input);
      } else {
        addMessage("assistant", "I didn't get that. How many minutes before do you want to be reminded? (or say 'none' to skip)");
        return;
      }
      proceed = true;
    } else if (pendingField === "contactName") {
      const name = input.trim();
      api('/api/contacts', {
        method: 'POST',
        body: JSON.stringify({
          name,
          email: '', phone: '', company: '',
          tags: [], source: 'Aidey Chat', isNew: true,
          notes: '', interactions: [],
        }),
      }).then(() => window.dispatchEvent(new Event('aidey:contactsUpdated')))
        .catch(err => console.error('Failed to save contact:', err));
      addMessage("assistant", `✅ Contact **${name}** added! You can add their email, phone and tags in the CRM.`);
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "details") {
      if (/^no|none|skip$/i.test(input.trim())) {
        task.details = "";
      } else {
        task.details = input.trim();
      }
      proceed = true;
    } else if (pendingField === "writingInput") {
      if (/^(no|none|skip|cancel)$/i.test(input.trim())) {
        addMessage("assistant", "Cancelled.");
        setPendingTask(null);
        setPendingField(null);
        return;
      }
      const writingText = input.trim();
      // Silently analyse the user's writing sample to build learned patterns
      analyseWritingSample(writingText, currentUser?.id);
      addLoadingMessage();
      const _styleProfile = loadStyleProfile(currentUser?.id);
      const _patterns = loadLearnedPatterns(currentUser?.id);
      const _systemPrompt = buildStyleSystemPrompt(_styleProfile, _patterns);
      setPendingTask(null);
      setPendingField(null);
      const _writingHistory = [
        ...chatMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .slice(-12)
          .map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: writingText },
      ];
      callAideyAI(_writingHistory, { system: _systemPrompt }).then((data) => {
        const suggestion = data?.reply?.trim() || null;
        setChatMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'assistant', content: suggestion || "I wasn't able to get a suggestion right now — try again in a moment.", ts: Date.now() },
        ]);
      });
      return;
    } else if (pendingField === "contentBrief") {
      if (/^no|none|skip$/i.test(input.trim())) {
        addMessage("assistant", "Cancelled.");
        setPendingTask(null);
        setPendingField(null);
        return;
      }
      const contentType = task.contentType || "content";
      const brief = input.trim();
      addLoadingMessage();
      const _styleProfile2 = loadStyleProfile(currentUser?.id);
      const _patterns2 = loadLearnedPatterns(currentUser?.id);
      const _systemPrompt2 = buildStyleSystemPrompt(_styleProfile2, _patterns2);
      setPendingTask(null);
      setPendingField(null);
      const _contentHistory = [
        ...chatMessages
          .filter((m) => m.role === 'user' || m.role === 'assistant')
          .slice(-12)
          .map((m) => ({ role: m.role, content: m.content })),
        { role: 'user', content: `Help me with this ${contentType}: ${brief}` },
      ];
      callAideyAI(_contentHistory, { system: _systemPrompt2 }).then((data) => {
        const suggestion = data?.reply?.trim() || null;
        setChatMessages(prev => [
          ...prev.slice(0, -1),
          { role: 'assistant', content: suggestion || `I wasn't able to fetch suggestions right now. Want me to create a task to track your ${contentType}?`, ts: Date.now() },
        ]);
      });
      return;
    } else if (pendingField === "createContentTask") {
      if (/^yes|yeah|ok|sure|create|yep/i.test(input.trim())) {
        const contentTask = {
          ...task,
          action: "create",
          title: `Draft ${task.contentType || "content"}: ${(task.details || "").slice(0, 40)}`,
          date: getTodayISO(),
          time: "09:00",
          priority: "Medium",
        };
        delete contentTask.contentBrief;
        setPendingTask(null);
        setPendingField(null);
        handleSingleAction(contentTask);
      } else {
        addMessage("assistant", "Cancelled.");
        setPendingTask(null);
        setPendingField(null);
      }
      return;
    } else if (pendingField === "openEmail") {
      if (/^yes|yeah|ok|sure|open|yep/i.test(input.trim())) {
        addMessage("assistant", "Opening Email Dashboard... Click 'Email' in the sidebar to get there.");
      } else {
        addMessage("assistant", "Cancelled.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "location") {
      if (/^no|none|skip$/i.test(input.trim())) {
        task.location = "";
      } else if (/^generate/i.test(input) && task.meetingType) {
        if (task.meetingType === "Google Meet") {
          // Check if Google is connected
          if (!isGoogleConnected()) {
            addMessage("assistant", "To generate Google Meet links, you need to connect your Google account first. Please connect Google Calendar from the main dashboard, then try again.");
            setPendingTask(task);
            setPendingField("location");
            return;
          }

          // Check if Google Meet API is available
          if (!isGoogleMeetAvailable()) {
            addMessage("assistant", "Google Meet API is not available. Please make sure Google Calendar is properly connected and try again.");
            setPendingTask(task);
            setPendingField("location");
            return;
          }

          addMessage("assistant", `Generating Google Meet link...`);

          // Create Google Meet meeting
          const meetingDetails = formatMeetingForGoogleMeet(task);

          createGoogleMeet(meetingDetails)
            .then((result) => {
              if (result.success && result.meetLink) {
                task.location = result.meetLink;
                task.googleMeetId = result.meetingId;
                addMessage("assistant", `✅ Google Meet link generated! Your meeting link: ${result.meetLink}`);
                setPendingTask(task);
                setPendingField(null);
                handleSingleAction(task);
              } else {
                addMessage("assistant", "Failed to generate Google Meet link. Please paste your meeting link manually.");
                setPendingTask(task);
                setPendingField("location");
              }
            })
            .catch((error) => {
              console.error('Google Meet creation error:', error);
              addMessage("assistant", `Error generating Google Meet link: ${error.message}. Please paste your meeting link manually.`);
              setPendingTask(task);
              setPendingField("location");
            });
          return;
        } else if (task.meetingType === "Teams") {
          // Check if Teams is connected
          if (!isTeamsConnected()) {
            addMessage("assistant", "To generate Microsoft Teams links, you need to connect your Microsoft Outlook account first. Please connect Outlook from the Profile section, then try again.");
            setPendingTask(task);
            setPendingField("location");
            return;
          }

          addMessage("assistant", `Generating Microsoft Teams link...`);

          // Create Teams meeting
          const meetingDetails = {
            title: task.title,
            date: task.date,
            time: task.time,
            duration: 60, // Default 1 hour
            details: task.details || `Microsoft Teams meeting for: ${task.title}`
          };

          createTeamsMeeting(meetingDetails)
            .then((result) => {
              if (result.success && result.meetingLink) {
                task.location = result.meetingLink;
                task.teamsEventId = result.eventId;
                addMessage("assistant", `✅ Microsoft Teams link generated! Your meeting link: ${result.meetingLink}`);
                setPendingTask(task);
                setPendingField(null);
                handleSingleAction(task);
              } else {
                addMessage("assistant", `Failed to generate Teams link: ${result.error || 'Unknown error'}. Please paste your meeting link manually.`);
                setPendingTask(task);
                setPendingField("location");
              }
            })
            .catch((error) => {
              console.error('Teams meeting creation error:', error);
              addMessage("assistant", `Error generating Teams link: ${error.message}. Please paste your meeting link manually.`);
              setPendingTask(task);
              setPendingField("location");
            });
          return;
        } else {
          // For other meeting types (custom, etc.)
          addMessage("assistant", `Generating a ${task.meetingType} link...`);
          // TODO: Integrate other meeting platforms here
          setTimeout(() => {
            addMessage("assistant", `Sorry, auto-generation for ${task.meetingType} isn't set up yet. Please paste your meeting link.`);
            setPendingTask(task);
            setPendingField("location");
          }, 1000);
          return;
        }
      } else if (/\b(online|virtual|remote|zoom|google\s*meet|teams|video\s*call|video\s*meeting)\b/i.test(input)) {
        // User is clarifying it's an online meeting — switch mode and ask for link
        task.meetingMode = "online";
        delete task.travelFrom; // no travel needed
        if (/zoom/i.test(input)) task.meetingType = "Zoom";
        else if (/google\s*meet/i.test(input)) task.meetingType = "Google Meet";
        else if (/teams/i.test(input)) task.meetingType = "Teams";
        else if (!task.meetingType) task.meetingType = "Other";
        const platform = task.meetingType || "meeting";
        setPendingTask(task);
        setPendingField("location");
        addMessage("assistant", `Got it — switching to an online ${platform}. Would you like to generate a ${platform} link or paste your own?`);
        return;
      } else {
        task.location = input.trim();
      }
      // For in-person meetings, ask where they're travelling from before creating
      if (task.meetingMode === "in person" && !task.hasOwnProperty('travelFrom')) {
        setPendingTask(task);
        setPendingField("travelFrom");
        addMessage("assistant", `Got it — ${task.location}. Will you need to travel to get there? If so, where are you heading from? (or say 'local' if you're already nearby)`);
        return;
      }
      proceed = true;
    } else if (pendingField === "travelFrom") {
      if (/^(?:local|nearby|no|nope|skip|close|already\s+there|I.?m\s+close)/i.test(input.trim())) {
        task.travelFrom = null;
        task.noTravel = true;
        proceed = true;
      } else if (/^walk/i.test(input.trim())) {
        task.travelFrom = null;
        task.travelMode = "walking";
        task.noTravel = true;
        proceed = true;
      } else {
        // Has a from-location — look up real travel time
        task.travelFrom = input.trim();
        addMessage("assistant", "Let me check travel times for you…");
        setPendingTask(task);
        setPendingField("arrangeTravel"); // hold here while async runs

        getTravelInfo(task.travelFrom, task.location).then(info => {
          task.travelInfo = info || null;

          if (info) {
            const { distanceKm, recommendedMode, travelMins, needsHotel, driving, transit } = info;
            const modeLabel = MODE_LABEL[recommendedMode] || recommendedMode;
            const modeEmoji = MODE_EMOJI[recommendedMode] || '🚗';
            // Pick the best duration text to show
            const byTrain = transit?.durationText;
            const byCar = driving?.durationText;
            let timeStr = '';
            if (recommendedMode === 'train' && byTrain) timeStr = `${byTrain} by train`;
            else if (recommendedMode === 'driving' && byCar) timeStr = `${byCar} by car`;
            else if (byTrain || byCar) timeStr = `${byTrain || byCar}`;
            else timeStr = `about ${travelMins} mins`;

            let msg = `${modeEmoji} ${task.travelFrom} to ${task.location} is ${timeStr} (${distanceKm} km). I'd suggest ${modeLabel}.`;
            if (needsHotel) msg += ` That's quite a distance — you may want to consider staying overnight.`;
            msg += `\n\nWould you like me to arrange the travel?`;

            // Check for same-day in-person meetings at other locations
            const clusterMatches = findSameDayInPersonMeetings(task, userSchedule);
            if (clusterMatches.length > 0) {
              const otherLocations = [...new Set(clusterMatches.map(t => t.location))].join(' and ');
              msg += `\n\n💡 You also have an in-person meeting at ${otherLocations} on the same day. Grouping them closer together could cut your travel costs — want me to suggest a better schedule?`;
            }

            setPendingTask({ ...task, travelInfo: info });
            addMessage("assistant", msg);
          } else {
            // API unavailable — ask manually
            setPendingTask(task);
            setPendingField("travelMode");
            addMessage("assistant", `How are you planning to get there — driving, train, flight, or taxi?`);
          }
        });
        return; // wait for async
      }
    } else if (pendingField === "travelMode") {
      // Fallback when no API data — user picks mode manually
      if (/fly|flight|plane|air/i.test(input)) task.travelMode = "flight";
      else if (/train|rail|tube|underground|metro/i.test(input)) task.travelMode = "train";
      else if (/coach|bus/i.test(input)) task.travelMode = "bus";
      else if (/driv|car/i.test(input)) task.travelMode = "driving";
      else if (/taxi|uber|cab/i.test(input)) task.travelMode = "taxi";
      else if (/walk/i.test(input)) task.travelMode = "walking";
      else task.travelMode = input.trim();
      proceed = true;
    } else if (pendingField === "arrangeTravel") {
      // User responds to "Would you like me to arrange the travel?"
      const answer = input.trim().toLowerCase();
      const info = task.travelInfo;
      const mode = task.travelMode || info?.recommendedMode || "driving";

      if (/^(?:no|nope|no\s+thanks|nah|i.?ll\s+sort|i.?ll\s+handle)/i.test(answer)) {
        // User will sort it themselves
        if (info) task.travelMode = info.recommendedMode;
        addMessage("assistant", `No problem — I'll block out ${info ? info.travelMins + ' mins' : 'travel time'} in your calendar.`);
        proceed = true;
      } else if (/driv|car/i.test(answer)) {
        task.travelMode = "driving";
        addMessage("assistant", `Got it, I'll block out ${info?.driving?.durationText || 'driving time'} for the journey.`);
        proceed = true;
      } else if (/walk/i.test(answer)) {
        task.travelMode = "walking";
        task.noTravel = true;
        proceed = true;
      } else if (/^(?:yes|yeah|yep|sure|please|ok|go\s+ahead|arrange)/i.test(answer)) {
        // Arrange it — build booking links and notes
        task.travelMode = mode;
        const links = getBookingLinks(task.travelFrom || '', task.location, mode);
        const modeEmoji = MODE_EMOJI[mode] || '🚗';
        const modeLabel = MODE_LABEL[mode] || mode;
        const bufferMins = info?.bufferMins || 75;

        let msg = `${modeEmoji} All sorted! I've blocked out ${bufferMins} mins for travel by ${modeLabel}.\n\n`;
        if (links.primary) msg += `📌 ${links.primary.label}: ${links.primary.url}\n`;
        if (links.secondary) msg += `📌 ${links.secondary.label}: ${links.secondary.url}\n`;
        if (info?.needsHotel && links.hotels) msg += `🏨 ${links.hotels.label}: ${links.hotels.url}\n`;
        msg = msg.trim();

        addMessage("assistant", msg);
        proceed = true;
      } else {
        // Didn't recognise — reprompt
        addMessage("assistant", `Just say yes to let me help, or tell me how you're getting there (driving, train, etc.)`);
        return;
      }
    } else if (pendingField === "followUp") {
      setPendingTask(null);
      setPendingField(null);
      if (/yes|sure|ok|follow.?up|calendar|invite/i.test(input)) {
        addMessage("assistant", "What should the follow-up be? Just tell me and I'll get it set up.");
      } else {
        addMessage("assistant", "All sorted! Let me know if there's anything else you need.");
      }
      return;
    } else if (pendingField === "meetingMode") {
      if (/online/i.test(input)) {
        task.meetingMode = "online";
        setPendingTask(task);
        setPendingField("meetingType");
        addMessage("assistant", "Which platform do you want to use for the online meeting? (Zoom, Google Meet, Teams, Other)");
        return;
      } else if (/in\s*person|office|address|room/i.test(input)) {
        task.meetingMode = "in person";
        setPendingTask(task);
        setPendingField("location");
        addMessage("assistant", "What is the address or room for the meeting?");
        return;
      } else if (/^yes|ok|sure$/i.test(input.trim())) {
        // If user just says "yes", assume they want online meeting (most common)
        task.meetingMode = "online";
        setPendingTask(task);
        setPendingField("meetingType");
        addMessage("assistant", "Great! Which platform do you want to use for the online meeting? (Zoom, Google Meet, Teams, Other)");
        return;
      } else {
        addMessage("assistant", "Please say 'in person' or 'online'.");
        return;
      }
    } else if (pendingField === "meetingType") {
      if (/zoom/i.test(input)) task.meetingType = "Zoom";
      else if (/google\s*meet|meet/i.test(input)) task.meetingType = "Google Meet";
      else if (/teams/i.test(input)) task.meetingType = "Teams";
      else task.meetingType = "Other";
      setPendingTask(task);
      setPendingField("location");
      addMessage("assistant", `Would you like to generate a ${task.meetingType} link or paste your own? (Say 'generate' or paste the link)`);
      return;
    } else if (pendingField === "resolveConflict") {
      const ans = input.trim();

      // Extract a specific time if the user named one ("move it to 3pm", "how about 4:30")
      const timeInAnswer = (() => {
        const m = ans.match(/\b(\d{1,2})(?:[.:h](\d{2}))?\s*(am|pm)\b/i)
          || ans.match(/\bmove\b.+\b(\d{1,2})(?:[.:h](\d{2}))?\s*(am|pm)?\b/i);
        if (!m) return null;
        let h = parseInt(m[1]), min = m[2] ? parseInt(m[2]) : 0;
        if (/pm/i.test(m[3]) && h < 12) h += 12;
        if (/am/i.test(m[3]) && h === 12) h = 0;
        if (!m[3] && h >= 1 && h <= 7) h += 12; // assume pm for ambiguous early afternoon
        return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
      })();

      const wantsOverride = /book\s+both|same\s+time|anyway|go\s+ahead|just\s+(do|book|create|add)\s+it|override|double.?book/i.test(ans);
      const wantsSwap = /move\s+(the\s+)?(other|existing|first|original|old)|swap(\s+them)?|shift\s+(it|the\s+other)/i.test(ans);
      const wantsReschedule = timeInAnswer
        || /^(yes|yeah|yep|sure|ok|that\s+works?|sounds?\s+good|perfect|great|go\s+for\s+it|move\s+it|reschedule)/i.test(ans)
        || /\b(book\s+it\s+(there|at\s+that|in\s+that|then)|use\s+(that\s+)?(slot|time|one)|put\s+it\s+there|that\s+slot|that\s+time)\b/i.test(ans);

      if (wantsOverride) {
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        addMessage("assistant", `Done — I've booked it in alongside the existing meeting.`);
        proceed = true;

      } else if (timeInAnswer) {
        // User gave an explicit time
        task.time = timeInAnswer;
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        addMessage("assistant", `Perfect, I'll book it at ${fmtTime(timeInAnswer)}.`);
        proceed = true;

      } else if (wantsSwap && task.conflictingEvent) {
        const clashTask = task.conflictingEvent;
        const clashNext = findNextAvailable(
          { date: clashTask.date, time: clashTask.time },
          userSchedule,
          task.durationMinutes
        );
        if (clashNext) {
          onMoveTask(clashTask.id, clashNext.date || clashTask.date, clashNext.time);
          addMessage("assistant", `I've moved "${clashTask.title}" to ${clashNext.dayOffset > 0 ? fmtDate(clashNext.date) + ' at ' : ''}${fmtTime(clashNext.time)} to free up the slot.`);
        } else {
          addMessage("assistant", `Couldn't find a free slot for "${clashTask.title}" — I'll book both at the same time.`);
        }
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        proceed = true;

      } else if (wantsReschedule && task.suggestion) {
        task.time = task.suggestion;
        if (task.suggestionDate) task.date = task.suggestionDate;
        delete task.suggestion; delete task.suggestionDate; delete task.conflictingEvent;
        addMessage("assistant", `Done — I'll put it at ${fmtTime(task.time)}.`);
        proceed = true;

      } else {
        // Didn't understand — give a gentle, specific re-prompt
        const hint = task.suggestion ? fmtTime(task.suggestion) : 'a different time';
        addMessage("assistant", `I can move it to ${hint}, shift the existing meeting out of the way, or book both at the same time — which works best?`);
        return;
      }
    } else if (pendingField === "attendeeEmailConfirm") {
      const { meeting, personName, email: existingEmail } = task;
      if (/^(yes|yeah|sure|ok|please|send|yep|go ahead)/i.test(input.trim())) {
        if (existingEmail) {
          const when = fmtDateTime(meeting?.date, meeting?.time);
          const subject = encodeURIComponent(`Invite: ${meeting?.title}`);
          const bodyText = `Hi ${personName},\n\nYou've been invited to:\n\n${meeting?.title}\n${when ? `When: ${when}\n` : ''}${meeting?.location ? `Where: ${meeting?.location}\n` : ''}\nLooking forward to seeing you there!`;
          const mailtoLink = `mailto:${existingEmail}?subject=${subject}&body=${encodeURIComponent(bodyText)}`;
          addMessage("assistant", `Here's the invite — click to open in your email app:\n\n📧 [Send invite to ${personName}](${mailtoLink})`);
        } else {
          addMessage("assistant", `What's ${personName}'s email address?`);
          setPendingTask(task);
          setPendingField("attendeeEmailAddress");
          return;
        }
      } else {
        addMessage("assistant", `No problem — ${personName} has been noted as an attendee.`);
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "attendeeEmailAddress") {
      const { meeting, personName } = task;
      const emailAddr = input.trim();
      if (emailAddr.includes('@')) {
        const when = fmtDateTime(meeting?.date, meeting?.time);
        const subject = encodeURIComponent(`Invite: ${meeting?.title}`);
        const bodyText = `Hi ${personName},\n\nYou've been invited to:\n\n${meeting?.title}\n${when ? `When: ${when}\n` : ''}${meeting?.location ? `Where: ${meeting?.location}\n` : ''}\nLooking forward to seeing you there!`;
        const mailtoLink = `mailto:${emailAddr}?subject=${subject}&body=${encodeURIComponent(bodyText)}`;
        addMessage("assistant", `Here's the invite — click to open in your email app:\n\n📧 [Send invite to ${personName}](${mailtoLink})`);
      } else {
        addMessage("assistant", `That doesn't look like a valid email. No worries — ${personName} has been noted as an attendee.`);
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "addAttendeeMeetingTitle") {
      const { personName, email } = task;
      const hint = input.trim();
      const meeting = userSchedule.find(t => t.title.toLowerCase().includes(hint.toLowerCase()));
      if (!meeting) {
        addMessage("assistant", `I couldn't find a meeting matching "${hint}". Can you give me more of the title?`);
        return;
      }
      const when = fmtDateTime(meeting.date, meeting.time);
      const emailLine = email
        ? `\n\nShall I draft an email invite for ${personName}?`
        : `\n\nDo you have an email address for ${personName} so I can send them an invite?`;
      addMessage("assistant", `Got it — adding ${personName} to "${meeting.title}"${when ? ` on ${when}` : ''}.${emailLine}`);
      setPendingTask({ ...task, meeting, email });
      setPendingField("attendeeEmailConfirm");
      return;
    } else if (pendingField === "suggestNextSlot") {
      if (/^yes|ok|sure$/i.test(input.trim()) && task.suggestion) {
        task.time = task.suggestion;
        delete task.suggestion;
        proceed = true;
      } else {
        addMessage("assistant", "What time should I book this for?");
        setPendingTask(task);
        setPendingField("time");
        return;
      }
    } else if (pendingField === "invoiceClient") {
      const name = input.trim();
      if (!task.amount) {
        addMessage("assistant", `Got it — invoice for **${name}**. What's the amount?`);
        setPendingTask({ ...task, client: name });
        setPendingField("invoiceAmount");
      } else {
        const preview = `📄 **Draft Invoice**\n**Client:** ${name}\n**Amount:** £${parseFloat(task.amount).toFixed(2)}\n**Status:** Draft\n\nDoes this look right? Say **"save it"** to add to your invoices, or **"cancel"** to start over.`;
        addMessage("assistant", preview);
        setPendingTask({ ...task, client: name });
        setPendingField("invoiceApproval");
      }
      return;
    } else if (pendingField === "invoiceAmount") {
      const amountMatch = input.match(/[$£€]?\s*(\d+(?:[.,]\d{2})?)/);
      if (!amountMatch) {
        addMessage("assistant", "I didn't catch that amount. Please enter a number, e.g. 500 or £1,200.00");
        return;
      }
      const amount = parseFloat(amountMatch[1].replace(',', '.'));
      const preview = `📄 **Draft Invoice**\n**Client:** ${task.client}\n**Amount:** £${amount.toFixed(2)}\n**Status:** Draft\n\nDoes this look right? Say **"save it"** to add to your invoices, or **"cancel"** to start over.`;
      addMessage("assistant", preview);
      setPendingTask({ ...task, amount });
      setPendingField("invoiceApproval");
      return;
    } else if (pendingField === "invoiceApproval") {
      if (/^(save|yes|yep|ok|sure|save it|looks good|send it|confirm|go ahead)/i.test(input.trim())) {
        api('/api/invoices', {
          method: 'POST',
          body: JSON.stringify({
            contactName: task.client || '',
            issueDate: new Date().toISOString().slice(0, 10),
            dueDate: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
            status: 'draft',
            lineItems: task.amount ? [{ id: 1, description: task.rawInput || 'Service', qty: 1, unitPrice: parseFloat(task.amount) || 0, tax: 0 }] : [],
          }),
        }).then(() => refreshInvoices()).catch(err => console.error('Failed to save invoice:', err));
        addMessage("assistant", `✅ Draft invoice saved for **${task.client}** (£${parseFloat(task.amount).toFixed(2)}). Head to **Invoicing** to add line items and send it to the client.`);
      } else {
        addMessage("assistant", "No problem — invoice cancelled. Let me know if you need anything else.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "emailTo") {
      const recipientInput = input.trim();
      let recipientEmail = null;
      // Check if it's already an email
      if (recipientInput.includes('@')) {
        recipientEmail = recipientInput;
      } else {
        const match = contacts.find(c => c.name?.toLowerCase().includes(recipientInput.toLowerCase()));
        recipientEmail = match?.email || null;
      }
      addMessage("assistant", `Email to **${recipientInput}**. What's it about?`);
      setPendingTask({ ...task, emailTo: recipientEmail || recipientInput, emailToName: recipientInput });
      setPendingField("emailSubject");
      return;
    } else if (pendingField === "emailSubject") {
      const subject = input.trim();
      addMessage("assistant", `Subject: "${subject}". What should the email say? (Say "generate" and I'll write a draft for you)`);
      setPendingTask({ ...task, emailSubject: subject });
      setPendingField("emailBody");
      return;
    } else if (pendingField === "emailBody") {
      const body = input.trim();
      const shouldGenerate = /^generate|^write it|^draft it/i.test(body);

      if (shouldGenerate) {
        addLoadingMessage();
        const prompt = `Write a short, professional email to ${task.emailToName || task.emailTo} about: ${task.emailSubject}. Keep it concise and friendly.`;
        callAideyAI([
          { role: 'user', content: prompt },
        ], {
          system: 'You are a professional email writing assistant. Write clear, concise emails. Do not include subject line in the body.',
        }).then((data) => {
          const draft = data?.reply?.trim() || null;
          const emailBody = draft || `Hi ${task.emailToName || 'there'},\n\nI wanted to reach out regarding ${task.emailSubject}.\n\nPlease let me know if you have any questions.\n\nBest regards`;
          const updatedTask = { ...task, emailBody };
          setPendingTask(updatedTask);
          const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'approval', approvalData: { tool: 'send_email_legacy', input: { to: task.emailTo, subject: task.emailSubject, body: emailBody } }, cardId, ts: Date.now() },
          ]);
        });
        return;
      }

      // User typed the body directly
      const updatedTask = { ...task, emailBody: body };
      setPendingTask(updatedTask);
      addApprovalCard({ tool: 'send_email_legacy', input: { to: task.emailTo, subject: task.emailSubject, body } });
      return;
    } else if (pendingField === "emailReview") {
      // Email review is now handled by the inline ApprovalCard — this branch is a
      // text-based fallback that keeps backward compatibility with voice input.
      if (/^(approve|send|send it|looks good|perfect|yes|ok|go ahead)/i.test(input.trim())) {
        const to = task.emailTo || '';
        const toEmail = to.includes('@') ? to : '';
        if (!toEmail) {
          addMessage("assistant", `I don't have an email address for ${task.emailTo}. What's their email?`);
          setPendingTask(task);
          setPendingField("emailRecipientAddress");
          return;
        }

        addLoadingMessage();
        api('/api/email/send', {
          method: 'POST',
          body: JSON.stringify({
            to: toEmail,
            subject: task.emailSubject || '(no subject)',
            body: task.emailBody || '',
          }),
        }).then(async (res) => {
          if (!res.ok) {
            const payload = await res.json().catch(() => ({}));
            throw new Error(payload?.error || 'Send failed');
          }
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'assistant', content: `Sent. Your email to **${toEmail}** is on its way.`, ts: Date.now() },
          ]);
        }).catch((err) => {
          setChatMessages(prev => [
            ...prev.slice(0, -1),
            { role: 'assistant', content: `I couldn't send that email: ${err.message}`, ts: Date.now() },
          ]);
        });
      } else if (/^edit|^change|^update/i.test(input.trim())) {
        // Fallback: if user typed "edit" as text, show them the card won't help here.
        addMessage("assistant", "Please use the Edit button on the approval card to modify the email body.");
        return;
      } else {
        addMessage("assistant", "Email discarded. Let me know if there's anything else I can help with.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === 'emailEditBody') {
      // Now handled inline by the ApprovalCard — skip gracefully.
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "emailRecipientAddress") {
      const addr = input.trim();
      if (addr.includes('@')) {
        const updatedEmailTask = { ...task, emailTo: addr };
        setPendingTask(updatedEmailTask);
        setPendingField(null);
        const cardId = `approval-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
        setChatMessages(prev => [...prev, { role: 'approval', approvalData: { tool: 'send_email_legacy', input: { to: addr, subject: updatedEmailTask.emailSubject, body: updatedEmailTask.emailBody } }, cardId, ts: Date.now() }]);
      } else {
        addMessage("assistant", "That doesn't look like a valid email. Email discarded — let me know if you'd like to try again.");
        setPendingTask(null);
        setPendingField(null);
      }
      return;
    } else if (pendingField === "bookingLinkRecipient") {
      const name = input.trim();
      const recipientMatch = contacts.find(c => c.name?.toLowerCase().includes(name.toLowerCase()));
      const recipientEmail = recipientMatch?.email || null;

      const bookingUrl = task.bookingUrl;
      const mailBody = `Hi ${name},\n\nFeel free to book a time that works for you:\n\n${bookingUrl}\n\nLooking forward to speaking!`;
      if (recipientEmail) {
        const link = `mailto:${recipientEmail}?subject=${encodeURIComponent('Book a time with me')}&body=${encodeURIComponent(mailBody)}`;
        addMessage("assistant", `Here's your booking link to share with **${name}**:\n\n📅 ${bookingUrl}\n\n[Open email to ${name}](${link})`);
        setPendingTask(null);
        setPendingField(null);
      } else {
        addMessage("assistant", `What's **${name}'s** email address?`);
        setPendingTask({ ...task, recipientName: name, mailBody });
        setPendingField("bookingLinkEmail");
      }
      return;
    } else if (pendingField === "bookingLinkEmail") {
      const addr = input.trim();
      const { recipientName, bookingUrl, mailBody } = task;
      if (addr.includes('@')) {
        const link = `mailto:${addr}?subject=${encodeURIComponent('Book a time with me')}&body=${encodeURIComponent(mailBody || `Hi,\n\nHere's my booking link:\n\n${bookingUrl}`)}`;
        addMessage("assistant", `[Open email to ${recipientName || addr}](${link})`);
      } else {
        addMessage("assistant", "That doesn't look like a valid email — let me know if you'd like to try again.");
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === "meetingNotes") {
      const allUsers = mentionableMembers;
      const attendeeIds = task.attendees || [];
      const attendees = attendeeIds.map(id => rosterMap.get(id)).filter(Boolean);

      const actionItems = parseMeetingNotes(input, allUsers);

      if (actionItems.length === 0) {
        addMessage("assistant",
          "I couldn't find any clear action items in those notes.\n\nTry formats like:\n• \"Ben will update the report\"\n• \"@carla send the proposals by Friday\"\n• \"- [ ] Fix login bug – Ben\"\n\nPaste the notes again using one of those patterns."
        );
        // stay in meetingNotes pending state
        return;
      }

      // Create a task for each action item — persist to the workspace-scoped API
      // (was a localStorage orphan that never reached the DB).
      const today = getTodayISO();
      const createdTasks = [];

      for (const item of actionItems) {
        const isDept = item.assigneeIds && item.assigneeIds.length > 0;
        const assignedTeam = isDept ? item.assigneeIds : (item.assigneeId ? [item.assigneeId] : []);
        const details = isDept
          ? `Action item for ${item.assigneeDept} department from meeting notes.`
          : "Action item from meeting notes.";
        try {
          await api('/api/tasks', {
            method: 'POST',
            body: JSON.stringify({
              title: item.title,
              priority: "Medium",
              date: today,
              datetime: new Date().toISOString(),
              taskType: "Meeting",
              assignedTeam,
              details,
            }),
          });
          const displayName = isDept
            ? `${item.assigneeDept} team (${item.assigneeIds.map(id => allUsers.find(u => u.id === id)?.name).filter(Boolean).join(", ")})`
            : (item.assigneeName || "Unassigned");
          createdTasks.push({ title: item.title, assignedTeam, userId: item.assigneeId, displayName });
        } catch (_) { /* skip a failed item, keep going */ }
      }

      window.dispatchEvent(new Event('aidey:tasksUpdated'));

      const taskLines = createdTasks
        .map(t => `• **${t.displayName}**: ${t.title}`)
        .join("\n");

      const attendeeNote = attendees.length > 0
        ? `\n\n📨 Summary shared with: ${attendees.map(u => u.name).join(", ")}`
        : "";

      const unassigned = createdTasks.filter(t => !t.assignedTeam?.length && t.displayName === "Unassigned").length;
      const assignmentNote = unassigned > 0
        ? `\n\n⚠ ${unassigned} task${unassigned > 1 ? "s" : ""} couldn't be assigned to a specific person — they'll appear in your task list. Add department/job title info in Team Management so Aidey can route these automatically next time.`
        : "";

      addMessage("assistant",
        `✅ Created ${createdTasks.length} action item${createdTasks.length !== 1 ? "s" : ""} from the meeting:\n\n${taskLines}${attendeeNote}${assignmentNote}\n\nThese are saved to your task list, and anyone they're assigned to will see them.`
      );

      setPendingTask(null);
      setPendingField(null);
      return;
    } else if (pendingField === 'meetingTranscriptReview') {
      if (/^(approve|yes|y|go ahead|confirm|create)/i.test(input.trim())) {
        const items = task.transcriptActionItems || [];
        if (!items.length) {
          addMessage('assistant', 'No transcript action items found to create.');
          setPendingTask(null);
          setPendingField(null);
          return;
        }

        const today = getTodayISO();
        const allUsers = mentionableMembers;
        const createdTasks = [];

        // Persist to the workspace-scoped tasks API (was a localStorage orphan).
        for (const item of items) {
          const isDept = item.assigneeIds && item.assigneeIds.length > 0;
          const assignedTeam = isDept ? item.assigneeIds : (item.assigneeId ? [item.assigneeId] : []);
          const details = isDept
            ? `Action item for ${item.assigneeDept} department from transcript.`
            : 'Action item from meeting transcript.';
          try {
            await api('/api/tasks', {
              method: 'POST',
              body: JSON.stringify({
                title: item.title,
                priority: 'Medium',
                date: today,
                datetime: new Date().toISOString(),
                taskType: 'Meeting',
                assignedTeam,
                details,
              }),
            });
            const displayName = isDept
              ? `${item.assigneeDept} team (${item.assigneeIds.map((id) => allUsers.find((u) => u.id === id)?.name).filter(Boolean).join(', ')})`
              : (item.assigneeName || 'Unassigned');
            createdTasks.push({ title: item.title, displayName });
          } catch (_) { /* skip a failed item, keep going */ }
        }

        window.dispatchEvent(new Event('aidey:tasksUpdated'));
        addMessage('assistant', `Created ${createdTasks.length} task(s) from the transcript, saved to your task list.`);
      } else {
        addMessage('assistant', 'No tasks created. Transcript note is saved.');
      }
      setPendingTask(null);
      setPendingField(null);
      return;
    }

    if (proceed) {
      setPendingTask(task);
      setPendingField(null);
      handleSingleAction(task);
    }
  }

  return isPopup ? (
    <>
      <style>{`
        .aidey-chat-scroll::-webkit-scrollbar {
          width: 8px;
        }
        .aidey-chat-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .aidey-chat-scroll::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .aidey-chat-scroll::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        .aidey-popup-chatbody::-webkit-scrollbar {
          width: 8px;
        }
        .aidey-popup-chatbody::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 4px;
        }
        .aidey-popup-chatbody::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 4px;
        }
        .aidey-popup-chatbody::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
        @keyframes aideyDot {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40%            { opacity: 1;   transform: scale(1); }
        }
        .aidey-typing-dots {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 0;
        }
        .aidey-typing-dots span {
          display: inline-block;
          width: 7px; height: 7px;
          border-radius: 50%;
          background: #999;
          animation: aideyDot 1.2s infinite ease-in-out;
        }
        .aidey-typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .aidey-typing-dots span:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
      {meetingModeActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', flexDirection: 'column' }}>
          <MeetingMode
            attendees={meetingModeAttendees}
            meetingTitle={meetingModeTitle}
            onEnd={handleMeetingEnd}
            onDismiss={handleMeetingDismiss}
          />
        </div>
      )}
      {zoomPanelOpen && !meetingModeActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 199, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <MeetingTranscripts onDismiss={() => setZoomPanelOpen(false)} />
        </div>
      )}
      {recorderOpen && !meetingModeActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, background: '#fff', overflow: 'auto' }}>
          <MeetingRecorder onClose={() => setRecorderOpen(false)} onCreateTask={onCreateMeetingTask} setFilter={setFilter} />
        </div>
      )}
      {meetingChoiceOpen && (
        <MeetingChoiceModal
          onTranscript={() => { setMeetingChoiceOpen(false); setZoomPanelOpen(true); }}
          onRecord={() => { setMeetingChoiceOpen(false); setRecorderOpen(true); }}
          onCancel={() => setMeetingChoiceOpen(false)}
        />
      )}
      <div className="aidey-popup-chatbody" ref={chatScrollRef} style={{ position: "relative" }}>
        {chatMessages.filter(m => !m.synthetic).map((m, i) => (
          m.role === 'approval' ? (
            <div key={i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0.17em 0' }}>
              <ApprovalCard
                approvalData={m.approvalData}
                onConfirm={(editedBody) => handleApprovalConfirm(m, editedBody)}
                onCancel={() => handleApprovalCancel(m)}
              />
            </div>
          ) : m.role === 'email_draft' ? (
            <div key={m.cardId || i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0.17em 0' }}>
              <EmailDraftCard
                draft={m.emailDraft}
                onSend={async (body) => {
                  try {
                    await api('/api/email/send', {
                      method: 'POST',
                      body: JSON.stringify({ to: m.emailDraft.to, subject: m.emailDraft.subject, body }),
                    });
                    setChatMessages(prev => prev.map(msg =>
                      msg.cardId === m.cardId
                        ? { role: 'assistant', content: `Sent. Your email to **${m.emailDraft.to}** is on its way.`, ts: Date.now() }
                        : msg
                    ));
                  } catch (err) {
                    setChatMessages(prev => prev.map(msg =>
                      msg.cardId === m.cardId
                        ? { role: 'assistant', content: `Couldn't send that email: ${err.message}`, ts: Date.now() }
                        : msg
                    ));
                  }
                }}
                onCancel={() => {
                  setChatMessages(prev => prev.map(msg =>
                    msg.cardId === m.cardId
                      ? { role: 'assistant', content: 'No problem, let me know if you need anything else.', ts: Date.now() }
                      : msg
                  ));
                }}
              />
            </div>
          ) : m.role === 'workload_redistribution' ? (
            <div key={m.cardId || i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0.17em 0' }}>
              <WorkloadRedistributionCard
                suggestions={m.suggestions || []}
                onConfirm={(approved) => handleWorkloadConfirm(m, approved)}
                onCancel={() => handleWorkloadCancel(m)}
              />
            </div>
          ) : m.role === 'absence_delegation' ? (
            <div key={m.cardId || i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0.17em 0' }}>
              <AbsenceDelegationCard
                absenceData={m.absenceData}
                leaveRequestId={m.leaveRequestId}
                onConfirm={(payload) => handleAbsenceDelegationConfirm(m, payload)}
                onCancel={() => handleAbsenceDelegationCancel(m)}
              />
            </div>
          ) : m.role === 'session_resume' ? (
            <div key={i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0.17em 0' }}>
              <SessionResumeCard
                sessionTitle={m.sessionTitle}
                sessionTime={m.sessionTime}
                onContinue={() => handleSessionContinue(m.sessionId)}
                onFresh={handleSessionFresh}
              />
            </div>
          ) : m.role === 'integration-required' ? (
            <div key={i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0.17em 0' }}>
              <IntegrationPrompt
                integrationName={m.integrationName}
                onNavigate={() => { window.dispatchEvent(new CustomEvent('aidey:goto-settings', { detail: 'video' })); }}
              />
            </div>
          ) : (
            <div
              key={i}
              className={`aidey-chat-message ${m.role}`}
              style={{
                background: m.role === "user" ? "#e4e69b" : "#f5f5f5",
                color: "#232323",
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                borderRadius: 10,
                padding: "0.62em 1.08em",
                margin: "0.17em 0",
                fontSize: "1.03rem",
                maxWidth: "83%",
                wordBreak: "break-word"
              }}
            >
              {m.isTyping ? (
                <span className="aidey-typing-dots">
                  <span /><span /><span />
                </span>
              ) : renderMessage(m.content)}
            </div>
          )
        ))}
      </div>

      {/* @Mention Suggestions Dropdown - Popup Version */}
      {showMentions && mentionSuggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          bottom: '60px',
          left: '10px',
          right: '10px',
          background: 'white',
          border: '1px solid #ddd',
          borderRadius: '8px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: '200px',
          overflowY: 'auto',
          zIndex: 1000
        }}>
          {mentionSuggestions.map((suggestion, index) => (
            <div
              key={suggestion.id}
              onClick={() => selectMention(suggestion)}
              style={{
                padding: '10px 15px',
                cursor: 'pointer',
                borderBottom: index < mentionSuggestions.length - 1 ? '1px solid #eee' : 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                background: 'white',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'white';
              }}
            >
              <div
                style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  backgroundColor: suggestion.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '12px',
                  fontWeight: '600'
                }}
              >
                {suggestion.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: '600', fontSize: '14px' }}>
                  {suggestion.handle}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {suggestion.name} • {suggestion.email}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Voice permission banner */}
      {permissionState === 'unknown' && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#f0f4f8', borderRadius: 8, marginBottom: '0.4rem', fontSize: '0.8rem', color: '#555', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>Aidey needs microphone access to take voice input. Your audio is processed instantly and never stored.</span>
          <button type="button" onClick={requestPermission} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif", whiteSpace: 'nowrap' }}>Allow access</button>
        </div>
      )}
      {permissionState === 'denied' && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#fff0f0', borderRadius: 8, marginBottom: '0.4rem', fontSize: '0.8rem', color: '#888' }}>
          Microphone access is blocked. Update your browser site permissions to enable voice input.
        </div>
      )}
      {/* Listening indicator */}
      {voiceState === 'listening' && (
        <div style={{ fontSize: '0.78rem', color: '#0C2D48', fontWeight: 600, marginBottom: '0.25rem', paddingLeft: '0.25rem' }}>Listening…</div>
      )}
      {/* Voice confirmation bar */}
      {voiceConfirmation && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#f0f4f8', borderRadius: 8, marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.83rem', color: '#555' }}>
          <span style={{ flex: 1 }}>Create <strong>{voiceConfirmation.intent.replace('create_', '')}</strong>: {voiceConfirmation.summary}</span>
          <button type="button" onClick={handleVoiceConfirm} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>Confirm</button>
          <button type="button" onClick={handleVoiceCancel} style={{ background: 'transparent', color: '#aaa', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>Cancel</button>
        </div>
      )}
      {isMeetingTranscribing && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#eef8ff', borderRadius: 8, marginBottom: '0.4rem', fontSize: '0.82rem', color: '#244a68' }}>
          <div style={{ marginBottom: '0.35rem' }}><strong>Live transcript:</strong> {liveMeetingTranscript || 'Listening...'}</div>
          <button type="button" onClick={stopMeetingTranscription} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>Stop transcribing</button>
        </div>
      )}
      {pendingTranscribePrompt && !isMeetingTranscribing && !meetingModeActive && (
        <div style={{ padding: '0.5rem 0.75rem', display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
          <button
            type="button"
            onClick={() => {
              const prompt = pendingTranscribePrompt;
              activateMeetingMode(
                Array.isArray(prompt.attendees) ? prompt.attendees : [],
                prompt.title || prompt
              );
            }}
            style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}
          >
            Yes, start notes
          </button>
          <button
            type="button"
            onClick={() => {
              setPendingTranscribePrompt(null);
              addMessage('assistant', 'No problem — just let me know if you need anything.');
            }}
            style={{ background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}
          >
            Not now
          </button>
        </div>
      )}
      <form
        onSubmit={pendingTask && pendingField ? e => {
          e.preventDefault();
          handleFieldInput(chatInput);
          setChatInput("");
        } : handleChatSubmit}
        className="aidey-popup-inputrow"
      >
        <input
          value={chatInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="aidey-popup-input"
          placeholder={isPersonalWorkspace
            ? "Ask me to add shopping, plan your day..."
            : "Ask me to book meetings, create tasks, or manage your schedule... (use @name to mention team members)"}
        />
        <VoiceInput voiceState={voiceState} onClick={startListening} />
        <button type="submit" className="aidey-popup-sendbtn">Send</button>
      </form>
    </>
  ) : (
    <div className="aidey-chat-card" style={{ background: "#fff", borderRadius: 12, boxShadow: "0 4px 18px rgba(0,0,0,0.09)", padding: "1.1rem 1rem 0.5rem", minHeight: fullPage ? "calc(100vh - 180px)" : 320, display: "flex", flexDirection: "column", position: 'relative' }}>
      <style>{`
        @keyframes aideyDot {
          0%, 80%, 100% { opacity: 0.2; transform: scale(0.8); }
          40%            { opacity: 1;   transform: scale(1); }
        }
        .aidey-typing-dots { display: inline-flex; align-items: center; gap: 4px; padding: 2px 0; }
        .aidey-typing-dots span { display: inline-block; width: 7px; height: 7px; border-radius: 50%; background: #999; animation: aideyDot 1.2s infinite ease-in-out; }
        .aidey-typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .aidey-typing-dots span:nth-child(3) { animation-delay: 0.4s; }
      `}</style>
      {meetingModeActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, borderRadius: 12, overflow: 'hidden' }}>
          <MeetingMode
            attendees={meetingModeAttendees}
            meetingTitle={meetingModeTitle}
            onEnd={handleMeetingEnd}
            onDismiss={handleMeetingDismiss}
          />
        </div>
      )}
      {zoomPanelOpen && !meetingModeActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 199, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
          <MeetingTranscripts onDismiss={() => setZoomPanelOpen(false)} />
        </div>
      )}
      {recorderOpen && !meetingModeActive && (
        <div style={{ position: 'absolute', inset: 0, zIndex: 200, borderRadius: 12, overflow: 'auto', background: '#fff' }}>
          <MeetingRecorder onClose={() => setRecorderOpen(false)} onCreateTask={onCreateMeetingTask} setFilter={setFilter} />
        </div>
      )}
      {meetingChoiceOpen && (
        <MeetingChoiceModal
          onTranscript={() => { setMeetingChoiceOpen(false); setZoomPanelOpen(true); }}
          onRecord={() => { setMeetingChoiceOpen(false); setRecorderOpen(true); }}
          onCancel={() => setMeetingChoiceOpen(false)}
        />
      )}
      {onClose && (
        <button
          onClick={handleClose}
          className="close-button"
          style={{
            position: "absolute",
            top: 12,
            right: 16,
            background: "#E4E69B",
            color: "#222",
            border: "none",
            borderRadius: 8,
            fontWeight: 600,
            fontSize: "1.1rem",
            padding: "0.35em 1.1em",
            zIndex: 9999,
            cursor: "pointer"
          }}
        >
          Close
        </button>
      )}
      <div
        className="aidey-chat-scroll"
        ref={chatScrollRef}
        style={{
          background: "#fafcff",
          borderRadius: "12px 12px 0 0",
          border: "1.5px solid #ececec",
          borderBottom: "1px solid #e9ecef",
          minHeight: 120,
          maxHeight: scrollable ? 250 : "none",
          height: fullPage ? "auto" : (scrollable ? "auto" : 280),
          flex: fullPage ? "1 1 auto" : "initial",
          padding: "1.2rem 1rem 0.5rem 1rem",
          marginBottom: "1rem",
          overflowY: (scrollable || fullPage) ? "auto" : "hidden",
          overflowX: "hidden",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {chatMessages.filter(m => !m.synthetic).map((m, i) => (
          m.role === 'approval' ? (
            <div key={i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0 0 8px 0' }}>
              <ApprovalCard
                approvalData={m.approvalData}
                onConfirm={(editedBody) => handleApprovalConfirm(m, editedBody)}
                onCancel={() => handleApprovalCancel(m)}
              />
            </div>
          ) : m.role === 'email_draft' ? (
            <div key={m.cardId || i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0 0 8px 0' }}>
              <EmailDraftCard
                draft={m.emailDraft}
                onSend={async (body) => {
                  try {
                    await api('/api/email/send', {
                      method: 'POST',
                      body: JSON.stringify({ to: m.emailDraft.to, subject: m.emailDraft.subject, body }),
                    });
                    setChatMessages(prev => prev.map(msg =>
                      msg.cardId === m.cardId
                        ? { role: 'assistant', content: `Sent. Your email to **${m.emailDraft.to}** is on its way.`, ts: Date.now() }
                        : msg
                    ));
                  } catch (err) {
                    setChatMessages(prev => prev.map(msg =>
                      msg.cardId === m.cardId
                        ? { role: 'assistant', content: `Couldn't send that email: ${err.message}`, ts: Date.now() }
                        : msg
                    ));
                  }
                }}
                onCancel={() => {
                  setChatMessages(prev => prev.map(msg =>
                    msg.cardId === m.cardId
                      ? { role: 'assistant', content: 'No problem, let me know if you need anything else.', ts: Date.now() }
                      : msg
                  ));
                }}
              />
            </div>
          ) : m.role === 'workload_redistribution' ? (
            <div key={m.cardId || i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0 0 8px 0' }}>
              <WorkloadRedistributionCard
                suggestions={m.suggestions || []}
                onConfirm={(approved) => handleWorkloadConfirm(m, approved)}
                onCancel={() => handleWorkloadCancel(m)}
              />
            </div>
          ) : m.role === 'absence_delegation' ? (
            <div key={m.cardId || i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0 0 8px 0' }}>
              <AbsenceDelegationCard
                absenceData={m.absenceData}
                leaveRequestId={m.leaveRequestId}
                onConfirm={(payload) => handleAbsenceDelegationConfirm(m, payload)}
                onCancel={() => handleAbsenceDelegationCancel(m)}
              />
            </div>
          ) : m.role === 'session_resume' ? (
            <div key={i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0 0 8px 0' }}>
              <SessionResumeCard
                sessionTitle={m.sessionTitle}
                sessionTime={m.sessionTime}
                onContinue={() => handleSessionContinue(m.sessionId)}
                onFresh={handleSessionFresh}
              />
            </div>
          ) : m.role === 'integration-required' ? (
            <div key={i} style={{ alignSelf: 'flex-start', width: '100%', maxWidth: '100%', margin: '0 0 8px 0' }}>
              <IntegrationPrompt
                integrationName={m.integrationName}
                onNavigate={() => { window.dispatchEvent(new CustomEvent('aidey:goto-settings', { detail: 'video' })); }}
              />
            </div>
          ) : (
            <div
              key={i}
              className={`aidey-chat-message ${m.role}`}
              style={{
                background: m.role === "user" ? "#e4e69b" : "#f5f5f5",
                color: m.role === "user" ? "#212121" : "#353535",
                alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                borderRadius: 10,
                padding: "0.65em 1.08em",
                margin: "0 0 8px 0",
                fontSize: "1.05rem",
                boxShadow: m.role === "assistant" ? "0 1px 3px #ececec" : undefined,
                maxWidth: "83%",
                wordBreak: "break-word"
              }}
            >
              {m.isTyping ? (
                <span className="aidey-typing-dots">
                  <span /><span /><span />
                </span>
              ) : renderMessage(m.content)}
            </div>
          )
        ))}
      </div>

      {/* @Mention Suggestions Dropdown - Regular Version */}
      {showMentions && mentionSuggestions.length > 0 && (
        <div style={{
          position: 'relative',
          marginTop: '5px'
        }}>
          <div style={{
            position: 'absolute',
            bottom: '100%',
            left: '0',
            right: '0',
            background: 'white',
            border: '1px solid #ddd',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            maxHeight: '200px',
            overflowY: 'auto',
            zIndex: 1000
          }}>
            {mentionSuggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                onClick={() => selectMention(suggestion)}
                style={{
                  padding: '10px 15px',
                  cursor: 'pointer',
                  borderBottom: index < mentionSuggestions.length - 1 ? '1px solid #eee' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: 'white',
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'white';
                }}
              >
                <div
                  style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: suggestion.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '12px',
                    fontWeight: '600'
                  }}
                >
                  {suggestion.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '14px' }}>
                    {suggestion.handle}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {suggestion.name} • {suggestion.email}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Voice permission banner */}
      {permissionState === 'unknown' && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#f0f4f8', borderRadius: 8, marginBottom: '0.4rem', marginTop: '0.4rem', fontSize: '0.8rem', color: '#555', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <span style={{ flex: 1 }}>Aidey needs microphone access to take voice input. Your audio is processed instantly and never stored.</span>
          <button type="button" onClick={requestPermission} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif", whiteSpace: 'nowrap' }}>Allow access</button>
        </div>
      )}
      {permissionState === 'denied' && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#fff0f0', borderRadius: 8, marginBottom: '0.4rem', marginTop: '0.4rem', fontSize: '0.8rem', color: '#888' }}>
          Microphone access is blocked. Update your browser site permissions to enable voice input.
        </div>
      )}
      {/* Listening indicator */}
      {voiceState === 'listening' && (
        <div style={{ fontSize: '0.78rem', color: '#0C2D48', fontWeight: 600, marginTop: '0.4rem', paddingLeft: '0.25rem' }}>Listening…</div>
      )}
      {/* Voice confirmation bar */}
      {voiceConfirmation && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#f0f4f8', borderRadius: 8, marginTop: '0.4rem', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap', fontSize: '0.83rem', color: '#555' }}>
          <span style={{ flex: 1 }}>Create <strong>{voiceConfirmation.intent.replace('create_', '')}</strong>: {voiceConfirmation.summary}</span>
          <button type="button" onClick={handleVoiceConfirm} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>Confirm</button>
          <button type="button" onClick={handleVoiceCancel} style={{ background: 'transparent', color: '#aaa', border: '1px solid #e0e0e0', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>Cancel</button>
        </div>
      )}
      {isMeetingTranscribing && (
        <div style={{ padding: '0.5rem 0.75rem', background: '#eef8ff', borderRadius: 8, marginBottom: '0.4rem', marginTop: '0.2rem', fontSize: '0.82rem', color: '#244a68' }}>
          <div style={{ marginBottom: '0.35rem' }}><strong>Live transcript:</strong> {liveMeetingTranscript || 'Listening...'}</div>
          <button type="button" onClick={stopMeetingTranscription} style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 6, padding: '0.25rem 0.65rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer', fontFamily: "'Raleway', sans-serif" }}>Stop transcribing</button>
        </div>
      )}
      {pendingTranscribePrompt && !isMeetingTranscribing && !meetingModeActive && (
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.4rem', marginBottom: '0.25rem' }}>
          <button
            type="button"
            onClick={() => {
              const prompt = pendingTranscribePrompt;
              activateMeetingMode(
                Array.isArray(prompt.attendees) ? prompt.attendees : [],
                prompt.title || prompt
              );
            }}
            style={{ background: '#0C2D48', color: '#fff', border: 'none', borderRadius: 8, padding: '0.45rem 1rem', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer' }}
          >
            Yes, start notes
          </button>
          <button
            type="button"
            onClick={() => {
              setPendingTranscribePrompt(null);
              addMessage('assistant', 'No problem — just let me know if you need anything.');
            }}
            style={{ background: 'transparent', color: '#555', border: '1px solid #ddd', borderRadius: 8, padding: '0.45rem 0.9rem', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
          >
            Not now
          </button>
        </div>
      )}
      <form
        onSubmit={pendingTask && pendingField ? e => {
          e.preventDefault();
          handleFieldInput(chatInput);
          setChatInput("");
        } : handleChatSubmit}
        style={{ display: "flex", gap: 10, marginTop: "0.6rem" }}
      >
        <input
          data-tour-id="chat-input"
          value={chatInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          className="dashboard-input"
          style={{
            fontSize: "1.08rem",
            padding: "0.74em 1em",
            borderRadius: 10
          }}
          placeholder={isPersonalWorkspace
            ? "Ask me to add shopping, plan your day..."
            : "Ask me to book meetings, create tasks, or manage your schedule! (use @name to mention team members)"}
        />
        <VoiceInput voiceState={voiceState} onClick={startListening} />
        <button type="submit" className="dashboard-btn" style={{ fontSize: "1.08rem" }}>
          Send
        </button>
      </form>
    </div>
  );
}
